import Vue from 'vue'
import Vuex, {Store} from 'vuex'
import AppRouter from '@/AppRouter'
import UserSetup from '@/models/UserSetup'
import AccessToken from '@/models/AccessToken'
import AuthenticationService from '@/services/AuthenticationService'
import InitialResponse from '@/models/InitialResponse'
import Plan, { PlanType } from '@/models/Plan'
import Role from '@/models/Role'
import User, {UserMostRecentView} from '@/models/User'
import Account from '@/models/Account'
import AccountsService from '@/services/AccountsService'
import ChannelsService from '@/services/ChannelsService'
import CommentsService from '@/services/CommentsService'
import CustomViewsService from '@/services/CustomViewsService'
import PlansService from '@/services/PlansService'
import FeedbackService from '@/services/FeedbackService'
import TacticsService from '@/services/TacticsService'
import UsersService from '@/services/UsersService'
import TagsService from '@/services/TagsService'
import InitiativesService from '@/services/InitiativesService'
import Subscription from '@/models/Subscription'
import Channel from '@/models/Channel'
import Tactic from '@/models/Tactic'
import axios from 'axios'
import AlertMessage, {AlertMessageSeverity} from '@/models/AlertMessage'
import Tag from '@/models/Tag'
import ID from '@/models/ID'
import TacticType from '@/models/TacticType'
// import TacticsImportMapping from '@/models/TacticsImportMapping' // Archiving TacticImport.vue - JV 04.18.23
import DuplicateSubscriptionEventMapping from '@/models/DuplicateSubscriptionEventMapping'
import UserRole from '@/models/UserRole'
import {rrulestr} from 'rrule'
import {isSameDay, differenceInMinutes, addMinutes} from 'date-fns'
import TacticPlatform from '@/models/TacticPlatform'
import BudgetsService from '@/services/BudgetsService'
import DocumentsService from '@/services/DocumentsService'
import PlanDocumentCategory from '@/models/PlanDocumentCategory'
import PlanDocument from '@/models/PlanDocument'
import TagCategory from '@/models/TagCategory'
import DashboardsService from './services/DashboardsService'
import Initiative from './models/Initiative'
import InitiativeType from './models/InitiativeType'
import InitiativeCustomization from './models/InitiativeCustomization'
import PlanningItem from './models/InterfacePlanningItem'

Vue.use(Vuex)
// Vuex.Store.prototype.$toast = Vue.prototype.$toast

let fetchTacticsRequestId = 0

// Copies profile fields edited on the account user onto the logged-in user.
// Never copies `accounts` or `isSuperAdmin`, which only the auth response provides.
function syncLoggedInUserFromAccountUser(loggedInUser: User, accountUser: User): void {
  loggedInUser.firstName = accountUser.firstName
  loggedInUser.lastName = accountUser.lastName
  loggedInUser.emailAddress = accountUser.emailAddress
  loggedInUser.profileImage = accountUser.profileImage
  loggedInUser.customViews = accountUser.customViews
  loggedInUser.mostRecentView = accountUser.mostRecentView
  loggedInUser.mostRecentPlanId = accountUser.mostRecentPlanId
  loggedInUser.status = accountUser.status
  loggedInUser.blockedTagIds = accountUser.blockedTagIds
  loggedInUser.rolesObserved = accountUser.rolesObserved
}

export default new Vuex.Store({
  state: {
    services: {
      accounts: new AccountsService(),
      authentication: new AuthenticationService(),
      channels: new ChannelsService(),
      comments: new CommentsService(),
      customViews: new CustomViewsService(),
      feedback: new FeedbackService(),
      plans: new PlansService(),
      tactics: new TacticsService(),
      users: new UsersService(),
      tags: new TagsService(),
      budgets: new BudgetsService(),
      documents: new DocumentsService(),
      dashboards: new DashboardsService(),
      initiatives: new InitiativesService(),
    },

    // Initialization and auth
    isAppInitialized: false as boolean,
    isUIBlocked: false as boolean,
    isUserLoggedIn: false as boolean,
    accessToken: {} as AccessToken,
    refreshAccessTokenTimer: {},
    shouldFlushedLocalstorage: 1679670964013,
    routeAccountIntId: 0 as number,
    localDevRefreshToken: '' as string, // In local dev environment, set this to value of refreshToken from set-cookie header in /authenticate response in order to maintain application state between browser refreshes

    // Global UI selections
    routeHistory: [] as {}[],
    currentDateRange: [] as Date[],
    monthViewDateRange: [] as Date[],
    dayViewDateRange: [] as Date[],
    rangeViewsDateRange: [] as Date[],
    monthRangeViewsDateRange: [] as Date[],
    initiativeSettingsDateRange: [] as Date[],
    recentDateRangesDictionary: {} as any,
    selectedChannelIntID: 0 as number,
    selectedTypeIntID: 0 as number,
    selectedPlatformIntID: 0 as number,
    isGlobalNavCollapsed: false as boolean, // Manages global nav state and relevant styles in other views
    isFeedbackModalOpen: false as boolean, // Manages feedback UI visibility
    isPrintDialogOpen: false as boolean, // Manages print layout dependencies throughout app

    // Lists of all objects of a certain type
    allAccounts: [] as Account[],
    allRoles: [] as Role[],

    // Currently active objects
    loggedInUser: new User() as User, // From the auth response; may not be a member of the current account
    currentUserIntID: 0 as number,
    currentAccountIntID: 0 as number,
    currentPlanIntID: 0 as number,
    currentTacticIntID: 0 as number,
    currentInitiativeIntID: 0 as number,

    // Filter state
    activeFiltersCount: '' as string, // badge expecting string
    visibleNestedPlansIntIds: [] as number[],
    selectedTacticTypeIntIds: [] as number[],
    selectedInitiativesIntIds: [] as number[],
    selectedInitiativeCustomizationsIntIds: [] as number[],
    selectedTagsIntIds: [] as number[],
    selectedSubscriptionsIntIds: [] as number[],
    isLeadPlanVisible: false as boolean,
    showLongRunningTacticsInInitiativesView: true as boolean,
    isNestedPlanFilterActive: false as boolean,
    isChannelFilterActive: false as boolean,
    isInitiativeFilterActive: false as boolean,
    filterNoInitiative: false as boolean,
    isTagFilterActive: false as boolean,
    filterNoTag: false as boolean,
    isSubscriptionFilterActive: false as boolean,

    // Archiving TacticImport.vue - JV 04.18.23
    // Tactic import
    // currentTacticMapping: {} as TacticsImportMapping,
    // currentImportedTacticRows: [] as object[],

    // Dictionaries
    // initiativeTypeNamesDict: {
    //   campaign: 'campaign',
    //   promotion: 'promotion',
    //   keyDate: 'key date',
    //   monthlyTheme: 'monthly theme',
    // } as {},
    socialTacticPlatformNamesDict: [
      'Twitter',
      'Facebook',
      'Instagram',
      'Pinterest',
      'LinkedIn',
      'Snapchat',
      'WhatsApp',
      'TikTok',
      'Tumblr',
      'Reddit',
    ] as string[],
    defaultChannelNamesSortOrder: [
      'Website',
      'Email',
      'Social Media',
      'Signage',
      'Collateral',
      'Direct Mail',
      'PR',
      'Advertising Assets',
      'Paid Search',
      'Social Advertising',
      'Display',
      'Print',
      'Out-of-Home',
      'Radio',
      'Cross Channel Video',
    ] as string[],
    monthlyFocusTypeName: 'monthly focus',
    initiativesPlanColor: '#A3B7C1' as string,
    initiativesPlanColorLight: '#C8D5DA' as string,
    tutorialUrlsDict: {
      accountOwner:
        'https://www.annumplanning.com/resources/guides/account-owner-tutorial/',
      planAdmin:
        'https://www.annumplanning.com/resources/guides/plan-admin-tutorial/',
      contributor:
        'https://www.annumplanning.com/resources/guides/contributor-tutorial/',
      viewer: 'https://www.annumplanning.com/resources/guides/viewer-tutorial/',
    },
  },

  getters: {
    // Initialization and auth
    services: (state) => state.services,
    isAppInitialized: (state) => state.isAppInitialized,
    isUIBlocked: (state) => state.isUIBlocked,
    isUserLoggedIn: (state) => state.isUserLoggedIn,
    accessToken: (state) => state.accessToken,
    mostRecentCoreView: (state, getters) => getters.currentUser.mostRecentView,
    routeAccountIntId: (state) => state.routeAccountIntId,

    // Global UI selections
    routeHistory: (state) => {
      return state.routeHistory
    },
    currentDateRange: (state) => {
      return state.currentDateRange
    },
    monthViewDateRange: (state) => {
      return state.monthViewDateRange
    },
    dayViewDateRange: (state) => {
      return state.dayViewDateRange
    },
    rangeViewsDateRange: (state) => {
      return state.rangeViewsDateRange
    },
    monthRangeViewsDateRange: (state) => {
      return state.monthRangeViewsDateRange
    },
    initiativeSettingsDateRange: (state) => {
      return state.initiativeSettingsDateRange
    },
    datePickerYearRange: () => {
      const now = new Date()
      return (
        String(now.getFullYear() - 10) + ':' + String(now.getFullYear() + 10)
      )
    },
    selectedChannel: (state, getters) => {
      const thisChannel = getters.currentChannels.find(
        (channel) => channel.id.intID === state.selectedChannelIntID
      )
      return thisChannel ? thisChannel : new Channel()
    },
    selectedType: (state, getters) => {
      const thisType = getters.currentTacticTypes.find(
        (type) => type.id.intID === state.selectedTypeIntID
      )
      return thisType ? thisType : new TacticType()
    },
    selectedPlatform: (state, getters) => {
      const thisPlatform = getters.currentTacticPlatforms.find(
        (platform) => platform.id.intID === state.selectedPlatformIntID
      )
      return thisPlatform ? thisPlatform : new TacticPlatform()
    },
    isGlobalNavCollapsed: (state) => state.isGlobalNavCollapsed,
    isFeedbackModalOpen: (state) => state.isFeedbackModalOpen,
    isPrintDialogOpen: (state) => state.isPrintDialogOpen,

    // Permissions and roles
    currentAccountPermissionLevel: (state, getters) => {
      const ownerIntID = getters.currentAccount?.ownerId?.intID
      const currentUserIntID = getters.currentUser?.id?.intID
      if (
        (ownerIntID && currentUserIntID === ownerIntID) ||
        getters.currentAccount?.userRoles?.some(
          (userRole) =>
            userRole?.userId?.intID === currentUserIntID &&
            userRole?.roleId?.intID === 1
        )
      ) {
        return Role.LEVEL_ACCOUNT_OWNER
      }
      if (getters.isCurrentUserSuperAdmin) {
        return Role.LEVEL_ACCOUNT_ADMIN
      }
      let lowestAccountPermissionLevel = 1000000
      getters.currentAccount?.userRoles?.forEach((userRole) => {
        if (userRole?.userId?.intID === getters.currentUser?.id?.intID) {
          if (
            userRole.roleId.intID === 5 &&
            lowestAccountPermissionLevel > Role.LEVEL_CONTRIBUTOR
          ) {
            lowestAccountPermissionLevel = Role.LEVEL_ACCOUNT_ADMIN
          } else if (
            userRole.roleId.intID === 4 &&
            lowestAccountPermissionLevel > Role.LEVEL_PLAN_ADMIN
          ) {
            lowestAccountPermissionLevel = Role.LEVEL_PLAN_ADMIN
          } else if (
            userRole.roleId.intID === 2 &&
            lowestAccountPermissionLevel > Role.LEVEL_ACCOUNT_ADMIN
          ) {
            lowestAccountPermissionLevel = Role.LEVEL_ACCOUNT_ADMIN
          } else if (
            userRole.roleId.intID === 1 &&
            lowestAccountPermissionLevel > Role.LEVEL_ACCOUNT_OWNER
          ) {
            lowestAccountPermissionLevel = Role.LEVEL_ACCOUNT_OWNER
          }
        }
      })
      return lowestAccountPermissionLevel < 1000000
        ? lowestAccountPermissionLevel
        : Role.LEVEL_VIEWER
    },
    currentPlanPermissionLevel: (state, getters) => {
      return getters.getPermissionLevelForPlanId(getters.currentPlan.id)
    },
    getPermissionLevelForPlanId: (state, getters) => (planId: ID) => {
      if (!planId) {
        return getters.currentAccountPermissionLevel
      }
      const thisUserRole = getters.getUserRoleforPlanId(planId)
      if (thisUserRole && thisUserRole.roleId) {
        return getters.getPermissionLevelForRoleId(thisUserRole.roleId)
      }
      return getters.currentAccountPermissionLevel
    },
    currentUserRole: (state, getters) => {
      const realUserRole = getters.getUserRoleforPlanId(getters.currentPlan.id)
      if (realUserRole) {
        return realUserRole
      }
      if (getters.isCurrentUserSuperAdmin && getters.currentPlan.id.intID !== 0) {
        // Stand-in role for a super admin with no role row on this plan.
        // It has no id (intID 0) and must never be sent to a service call.
        const standInUserRole = new UserRole()
        standInUserRole.canViewBudget = true
        standInUserRole.canEditAllChannels = true
        standInUserRole.channels = getters.currentPlan.channels.map(
          (channel: Channel) => channel.id
        )
        standInUserRole.planId = getters.currentPlan.id
        standInUserRole.userId = getters.currentUser.id
        standInUserRole.accountId = getters.currentAccount.id
        return standInUserRole
      }
      return new UserRole()
    },
    getUserRoleforPlanId: (state, getters) => (planId: ID) => {
      const thisPlan = getters.allPlans.find(
        (plan) => plan.id.intID == planId.intID
      )
      if (thisPlan && thisPlan.userRoles) {
        const planUserRolesForCurrentUser = thisPlan.userRoles.filter(
          (userRole) => userRole.userId.intID == getters.currentUser.id.intID
        )
        if (planUserRolesForCurrentUser.length === 1) {
          return planUserRolesForCurrentUser[0]
        } else if (planUserRolesForCurrentUser.length > 1) {
          let lowestPermissionLevel = 1000000
          let lowestUserRole = null
          planUserRolesForCurrentUser.forEach((userRole) => {
            const thisPermissionLevel = getters.getPermissionLevelForRoleId(
              userRole.id
            )
            if (thisPermissionLevel < lowestPermissionLevel) {
              lowestPermissionLevel = thisPermissionLevel
              lowestUserRole = userRole
            }
          })
          return lowestUserRole
        } else {
          return null
        }
      }
      return null
    },
    getPermissionLevelForRoleId: (state, getters) => (roleId: ID) => {
      if (roleId.intID !== 0) {
        const thisRole = getters.allRoles.find(
          (role) => role.id.intID == roleId.intID
        )
        if (thisRole) {
          return thisRole.level
        }
      }
      return null
    },

    // Lists of all objects of a certain type
    currentSubscriptions: (state, getters) =>
      getters.allSubscriptions.filter((subscription) =>
        getters.currentPlan.subscriptionIds
          .map((id) => id.intID)
          .includes(subscription.id.intID)
      ),
    allSubscriptions: (state, getters) => getters.currentAccount.subscriptions,
    allAccounts: (state) => {
      const usedIds = [] as number[] // API is returning multiple instances of the same account, this is in place to filter out duplicates
      const returnArray = state.allAccounts.filter((account) => {
        if (!usedIds.includes(account.id.intID)) {
          usedIds.push(account.id.intID)
          return account
        }
      })
      return returnArray.sort((a, b) => {
        const textA = a.contactCompanyName.toLowerCase()
        const textB = b.contactCompanyName.toLowerCase()
        return textA < textB ? -1 : textA > textB ? 1 : 0
      })
    },
    allPlans: (state, getters) => {
      if (getters.currentAccount && getters.currentAccount.plans) {
        const planSort = (a: Plan, b: Plan) => {
          const aValid = a.orderIndex > 0
          const bValid = b.orderIndex > 0
          if (aValid && bValid) return a.orderIndex - b.orderIndex
          if (aValid) return -1
          if (bValid) return 1
          return b.id.intID - a.id.intID
        }

        const currentPlans = getters.currentAccount.plans
          .filter((plan: Plan) => !plan.isNested)
          .sort(planSort)

        // add nested plans back in array just behind their Lead Plan, sorted by orderIndex
        getters.currentAccount.plans
          .filter((plan: Plan) => plan.isNested)
          .sort((a: Plan, b: Plan) => a.orderIndex - b.orderIndex)
          .forEach((nestedPlan: Plan) => {
            // find the index of their Lead Plan
            const leadPlanIndex = currentPlans.findIndex(
              (leadPlan) => leadPlan.id.intID == nestedPlan.parentId.intID
            )
            // find insertion point after lead plan and any already-inserted siblings
            let insertIndex = leadPlanIndex + 1
            while (
              insertIndex < currentPlans.length &&
              currentPlans[insertIndex].isNested &&
              currentPlans[insertIndex].parentId.intID === nestedPlan.parentId.intID
            ) {
              insertIndex++
            }
            currentPlans.splice(insertIndex, 0, nestedPlan)
          })

        return currentPlans
      }
      return []
    },
    allRoles: (state) => state.allRoles,

    // Currently active objects
    allAccountUsers: (state, getters) => getters.currentAccount.users,
    isCurrentUserSet: (state, getters) =>
      getters.currentUser.id && getters.currentUser.id.intID != 0
        ? true
        : false,
    currentUserIntID: (state) => state.currentUserIntID,
    isCurrentUserSuperAdmin: (state) => state.loggedInUser.isSuperAdmin === true,
    currentUser: (state, getters) => {
      const thisUser = getters.allAccountUsers.find(
        (user) => user.id.intID === getters.currentUserIntID
      )
      if (thisUser) {
        return thisUser
      }
      // Not a member of the current account (e.g. a super admin): fall back to the auth-response user
      if (
        state.loggedInUser.id.intID !== 0 &&
        state.loggedInUser.id.intID === getters.currentUserIntID
      ) {
        return state.loggedInUser
      }
      return new User()
    },
    currentAccountUniqueUserRoles: (state, getters) => {
      const uniqueUserIntIds = [] as number[]
      const uniqueUserRoles = [] as UserRole[]
      getters.currentAccount.userRoles.forEach((userRole) => {
        if (!uniqueUserIntIds.includes(userRole.userId.intID)) {
          uniqueUserRoles.push(userRole)
          uniqueUserIntIds.push(userRole.userId.intID)
        }
      })
      return uniqueUserRoles
    },
    currentPlanUserRoles: (state, getters) =>
      getters.currentPlan.userRoles.filter(
        (userRole) => userRole.planId.intID == getters.currentPlan.id.intID
      ),
    currentAccountIntID: (state) => state.currentAccountIntID,
    currentAccount: (state, getters) => {
      const thisAccount = getters.allAccounts.find(
        (account) => account.id.intID === getters.currentAccountIntID
      )
      return thisAccount ? thisAccount : new Account()
    },
    currentPlanIntID: (state) => state.currentPlanIntID,
    currentPlan: (state, getters) => {
      let returnValue: Plan = new Plan()
      if (getters.currentAccount && getters.currentAccount.plans) {
        returnValue = getters.currentAccount.plans.find(
          (plan) => plan.id.intID == getters.currentPlanIntID
        )
      }
      return returnValue ? returnValue : new Plan()
    },
    currentChannels: (state, getters) => {
      const returnArray = [] as Channel[]
      if (getters.currentPlan.channels) {
        getters.currentPlan.channels.filter((channel) => {
          if (channel.name !== Channel.CHANNEL_NAME_INITIATIVES) {
            returnArray.push(channel)
          }
        })
      }
      //Sort channels by specific order - default channels according to defaultChannelNamesSortOrder first, then any user-generated channels
      const sortByObject = state.defaultChannelNamesSortOrder.reduce(
        (a, c, i) => {
          a[c] = i
          return a
        },
        {}
      )
      return returnArray
        .sort((a, b) => sortByObject[a.name] - sortByObject[b.name])
        .sort((a, b) => {
          if (a.orderIndex === b.orderIndex) {
            return 0
          }
          return a.orderIndex > b.orderIndex ? 1 : -1
        })
    },
    currentTacticTypes: (state, getters) => {
      const returnArray = [] as TacticType[]
      getters.currentChannels.forEach((channel) => {
        returnArray.push(...channel.tacticTypes)
      })
      return returnArray
    },
    currentTacticPlatforms: (state, getters) => {
      const returnArray = [] as TacticPlatform[]
      getters.currentTacticTypes.forEach((type) => {
        returnArray.push(...type.tacticPlatforms)
      })
      return [
        ...new Map(returnArray.map((item) => [item.id.intID, item])).values(),
      ]
    },
    currentInitiatives: (state, getters) => {
      return getters.currentPlan.initiatives
    },
    currentInitiativeCustomizations: (state, getters) => {
      const returnArray = [] as InitiativeCustomization[]
      getters.currentPlan.initiatives.forEach((initiative) => {
        returnArray.push(...initiative.initiativeCustomizations)
      })
      return returnArray
    },
    allInitiativesInCategories: (state, getters) => {
      return getters.currentPlan.initiativeChannel.sortedInitiativeTypes
    },
    currentTags: (state, getters) => getters.currentPlan.tags,
    currentPlanTagCategories: (state, getters) => getters.currentPlan.tagCategories,
    currentTacticIntID: (state) => state.currentTacticIntID,
    currentTactic: (state, getters) => {
      let returnValue: Tactic = new Tactic()
      if (getters.currentPlan && getters.currentPlan.tactics) {
        returnValue = getters.currentPlan.tactics.find(
          (tactic) => tactic.id.intID == getters.currentTacticIntID
        )
      }
      return returnValue ? returnValue : new Tactic()
    },
    currentInitiativeIntID: (state) => state.currentInitiativeIntID,
    currentInitiative: (state, getters) => {
      let returnValue: Initiative = new Initiative()
      if (getters.currentPlan && getters.currentPlan.tactics) {
        returnValue = getters.currentPlan.initiatives.find(
          (initiative) => initiative.id.intID == getters.currentInitiativeIntID
        )
      }
      return returnValue ? returnValue : new Initiative()
    },

    // Filter state
    activeFiltersCount: (state) => state.activeFiltersCount,
    isLeadPlanVisible: (state) => state.isLeadPlanVisible,
    showLongRunningTacticsInInitiativesView: (state) => state.showLongRunningTacticsInInitiativesView,
    visibleNestedPlansIntIds: (state) => state.visibleNestedPlansIntIds,
    isChannelFilterActive: (state) => state.isChannelFilterActive,
    isInitiativeFilterActive: (state) => state.isInitiativeFilterActive,
    filterNoInitiative: (state) => state.filterNoInitiative,
    isTagFilterActive: (state) => state.isTagFilterActive,
    filterNoTag: (state) => state.filterNoTag,
    isSubscriptionFilterActive: (state) => state.isSubscriptionFilterActive,
    selectedChannels: (state, getters) => {
      return getters.currentChannels.filter((channel) => 
        channel.tacticTypes.filter((type) => state.selectedTacticTypeIntIds.includes(type.id.intID)).length ? true : false
      )
    },
    selectedTacticTypes: (state, getters) => {
      return getters.currentTacticTypes.filter((type) =>
        state.selectedTacticTypeIntIds.includes(type.id.intID)
      )
    },
    // Set-based getters for O(1) membership lookups
    selectedChannelIdSet: (_state, getters) => {
      return new Set(getters.selectedChannels.map((c: Channel) => c.id.intID))
    },
    selectedTacticTypeIdSet: (state) => {
      return new Set(state.selectedTacticTypeIntIds)
    },
    paidChannelNamesSet: (_state, getters) => {
      return new Set(
        getters.currentChannels
          .filter((channel: Channel) => channel.isPaidMedia)
          .map((channel: Channel) => channel.name.toLowerCase())
      )
    },
    selectedTagIdSet: (_state, getters) => {
      return new Set(getters.selectedTags.map((t: Tag) => t.id.intID))
    },
    selectedInitiativeIdSet: (state) => {
      return new Set(state.selectedInitiativesIntIds)
    },
    selectedInitiativeCustomizationIdSet: (state) => {
      return new Set(state.selectedInitiativeCustomizationsIntIds)
    },
    selectedInitiatives: (state, getters) => {
      if(state.selectedInitiativesIntIds.length){
        return getters.currentInitiatives.filter((initiative) => state.selectedInitiativesIntIds.includes(initiative.id.intID))
      }else if(state.isInitiativeFilterActive){
        // When initiative filter is active but no initiatives selected (e.g., only customizations selected),
        // return empty array so parent initiatives are filtered out
        return []
      }else{
        return getters.currentInitiatives
      }
    },
    selectedInitiativeCustomizations: (state, getters) => {
      return getters.currentInitiativeCustomizations.filter((customization) => {
        if(state.selectedInitiativeCustomizationsIntIds.includes(customization.id.intID)){
          if(!getters.currentPlan.isNested || (getters.currentPlan.isNested && customization.planId.intID === getters.currentPlan.id.intID)){
            return true
          }
        }
        return false
      })
    },
    selectedInitiativesInCategories: (state, getters) => {
      const returnArray = [] as InitiativeType[]

      getters.currentPlan.initiativeChannel.sortedInitiativeTypes.forEach((type)=>{
        const thisType = {...type, key: type.key, initiatives: [] as PlanningItem[]}
        getters.selectedInitiatives.forEach((initiative) => {
          if((initiative.initiativeType.id.intID === type.id.intID)){
            thisType.initiatives.push(initiative)
          }
        })
        getters.selectedInitiativeCustomizations.forEach((customization) => {
          if((customization.initiativeTypeId.intID === type.id.intID)){
            thisType.initiatives.push(customization)
          }
        })
        returnArray.push(thisType)
      })

      return returnArray
    },
    selectedTags: (state, getters) => {
      return getters.currentTags.filter((tag) =>
        state.selectedTagsIntIds.includes(tag.id.intID)
      )
    },
    selectedSubscriptions: (state, getters) => {
      return getters.currentSubscriptions.filter((subscription) =>
        state.selectedSubscriptionsIntIds.includes(subscription.id.intID)
      )
    },
    deselectedInitiatives: (state, getters) =>
      getters.currentInitiatives.filter(
        (initiative) =>
          !getters.selectedInitiatives.filter(
            (selectedInitiative) => selectedInitiative.id.intID == initiative.id.intID
          ).length
      ),
    deselectedTags: (state, getters) =>
      getters.currentTags.filter(
        (tag) =>
          !getters.selectedTags.filter(
            (selectedTag) => selectedTag.id.intID == tag.id.intID
          ).length
      ),

    // Dictionaries
    paidChannelNames: (state, getters) => {
      return getters.currentChannels.map((channel) => {
        if (channel.isPaidMedia) {
          return channel.name.toLowerCase()
        }
      })
    },
    // initiativeTypeNamesDict: (state) => state.initiativeTypeNamesDict,
    socialTacticPlatformNamesDict: (state) =>
      state.socialTacticPlatformNamesDict,
    monthlyFocusTypeName: (state) => state.monthlyFocusTypeName,
    initiativesPlanColor: (state) => state.initiativesPlanColor,
    initiativesPlanColorLight: (state) => state.initiativesPlanColorLight,
    tutorialUrlsDict: (state) => state.tutorialUrlsDict,

    // Archiving TacticImport.vue - JV 04.18.23
    // Tactic mapping
    // currentTacticMapping: (state) => state.currentTacticMapping,
    // currentImportedTacticRows: (state) => state.currentImportedTacticRows,

    // Query string helpers
    currentDateStartQueryString: (state) => {
      if (!state.currentDateRange[0]) {
        return ''
      }
      return (
        state.currentDateRange[0].getMonth() +
        1 +
        '-' +
        state.currentDateRange[0].getDate() +
        '-' +
        state.currentDateRange[0].getFullYear()
      )
    },
    currentDateEndQueryString: (state) => {
      if (!state.currentDateRange[1]) {
        return ''
      }
      return (
        state.currentDateRange[1].getMonth() +
        1 +
        '-' +
        state.currentDateRange[1].getDate() +
        '-' +
        state.currentDateRange[1].getFullYear()
      )
    },
    isLeadPlanVisibleQueryString: (state, getters) => {
      return getters.isLeadPlanVisible ? 'true' : 'false'
    },
    visibleNestedPlansQueryString: (state, getters) => {
      let outputString = ''
      getters.visibleNestedPlansIntIds.forEach((element) => {
        outputString += element + ','
      })
      return outputString.slice(0, outputString.length - 1)
    },
    selectedTacticTypesQueryString: (state, getters) => {
      let outputString = ''
      getters.selectedTacticTypes.forEach((element) => {
        outputString += element.id.intID + ','
      })
      return outputString.slice(0, outputString.length - 1)
    },
    selectedInitiativesQueryString: (state, getters) => {
      let outputString = ''
      getters.selectedInitiatives.forEach((element) => {
        outputString += element.id.intID + ','
      })
      return outputString.slice(0, outputString.length - 1)
    },
    selectedTagsQueryString: (state, getters) => {
      let outputString = ''
      getters.selectedTags.forEach((element) => {
        outputString += element.id.intID + ','
      })
      return outputString.slice(0, outputString.length - 1)
    },
    selectedSubscriptionsQueryString: (state, getters) => {
      let outputString = ''
      getters.selectedSubscriptions.forEach((element) => {
        outputString += element.id.intID + ','
      })
      return outputString.slice(0, outputString.length - 1)
    },
  },

  mutations: {
    addRouteToRouteHistory: function(state, newRoute: {}){
      if(!state.routeHistory || !state.routeHistory.length){
        state.routeHistory = [{...newRoute, matched: null, order: 0}]
      }else{
        state.routeHistory.push({...newRoute, matched: null, order: state.routeHistory.length})
      }
    },
    updateIsUserLoggedIn: function (state, newValue: boolean) {
      Vue.set(state, 'isUserLoggedIn', newValue)
    },
    updateLoggedInUser: function (state, user: User) {
      Vue.set(state, 'loggedInUser', user)
    },
    updateIsAppInitialized: function (state, newValue: boolean) {
      Vue.set(state, 'isAppInitialized', newValue)
    },
    updateIsUIBlocked: function (state, newValue: boolean) {
      Vue.set(state, 'isUIBlocked', newValue)
    },
    updateIsGlobalNavCollapsed: function (state, newValue: boolean) {
      Vue.set(state, 'isGlobalNavCollapsed', newValue)
    },
    updateIsPrintDialogOpen: function (state, newValue: boolean) {
      Vue.set(state, 'isPrintDialogOpen', newValue)
    },
    updateIsLeadPlanVisible: function (state, newValue: boolean) {
      Vue.set(state, 'isLeadPlanVisible', newValue)
    },
    updateShowLongRunningTacticsInInitiativesView: function (state, newValue: boolean) {
      Vue.set(state, 'showLongRunningTacticsInInitiativesView', newValue)
    },
    updateVisibleNestedPlansIntIds: function (state, newPlanIds: number[]) {
      Vue.set(state, 'visibleNestedPlansIntIds', newPlanIds)
    },
    updateSelectedTacticTypes: function (state, newTypes: TacticType[]) {
      Vue.set(state, 'selectedTacticTypeIntIds', newTypes.map((type) => type.id.intID))
    },
    updateSelectedInitiatives: function (state, newInitiatives: Initiative[]) {
      Vue.set(state, 'selectedInitiativesIntIds', newInitiatives.map((initiative) => initiative.id.intID))
    },
    updateSelectedInitiativeCustomizations: function (state, newInitiativeCustomizations: InitiativeCustomization[]) {
      Vue.set(state, 'selectedInitiativeCustomizationsIntIds', newInitiativeCustomizations.map((customization) => customization.id.intID))
    },
    updateSelectedTags: function (state, newTags: Tag[]) {
      Vue.set(state, 'selectedTagsIntIds', newTags.map((tag) => tag.id.intID))
    },
    updateSelectedSubscriptions: function (state, newSubscriptions: Subscription[]) {
      Vue.set(state, 'selectedSubscriptionsIntIds', newSubscriptions.map((subscription) => subscription.id.intID))
    },

    isNestedPlanFilterActive: function (state, newValue: boolean) {
      Vue.set(state, 'isNestedPlanFilterActive', newValue)
    },
    isChannelFilterActive: function (state, newValue: boolean) {
      Vue.set(state, 'isChannelFilterActive', newValue)
    },
    isInitiativeFilterActive: function (state, newValue: boolean) {
      Vue.set(state, 'isInitiativeFilterActive', newValue)
    },
    filterNoInitiative: function (state, newValue: boolean) {
      Vue.set(state, 'filterNoInitiative', newValue)
    },
    isTagFilterActive: function (state, newValue: boolean) {
      Vue.set(state, 'isTagFilterActive', newValue)
    },
    filterNoTag: function (state, newValue: boolean) {
      Vue.set(state, 'filterNoTag', newValue)
    },
    isSubscriptionFilterActive: function (state, newValue: boolean) {
      Vue.set(state, 'isSubscriptionFilterActive', newValue)
    },

    updateCurrentPlan: function (state, newPlan: Plan) {
      Vue.set(state, 'currentPlanIntID', newPlan.id.intID)
    },
    updateCurrentPlanIntID: function (state, intID: number) {
      Vue.set(state, 'currentPlanIntID', intID)
    },
    updateCurrentAccount: function (state, newAccount) {
      if (state.allAccounts.length === 0) {
        state.allAccounts.push(newAccount)
      } else {
        state.allAccounts = state.allAccounts.map((account) => {
          if (account.id.intID === newAccount.id.intID) {
            return newAccount
          }
          return account
        })
      }
      Vue.set(state, 'currentAccountIntID', newAccount.id.intID)
      Vue.set(state, 'routeAccountIntId', newAccount.id.intID)
      window.localStorage.setItem('act', newAccount.id.intID.toString())
    },
    setCurrentTacticIntID: function (state, tacticIntID: number) {
      Vue.set(state, 'currentTacticIntID', tacticIntID)
    },
    setCurrentInitiativeIntID: function (state, initiativeIntID: number) {
      Vue.set(state, 'currentInitiativeIntID', initiativeIntID)
    },

    updateSelectedChannel: function (state, channel: Channel) {
      Vue.set(state, 'selectedChannelIntID', channel?.id?.intID)
    },
    updateSelectedType: function (state, type: TacticType) {
      Vue.set(state, 'selectedTypeIntID', type?.id?.intID)
    },
    updateSelectedPlatform: function (state, platform: TacticPlatform) {
      Vue.set(state, 'selectedPlatformIntID', platform?.id?.intID)
    },

    // Archiving TacticImport.vue - JV 04.18.23
    // updateCurrentTacticMapping: function (
    //   state,
    //   mapping: TacticsImportMapping
    // ) {
    //   Vue.set(state, 'currentTacticMapping', mapping)
    // },
    // updateCurrentImportedTacticRows: function (state, rows: object[]) {
    //   Vue.set(state, 'currentImportedTacticRows', rows)
    // },

    updateIsFeedbackModalOpen: function (state, newValue: boolean) {
      Vue.set(state, 'isFeedbackModalOpen', newValue)
    },

    updateRouteAccountIntId: function (state, newIntId: number) {
      Vue.set(state, 'routeAccountIntId', newIntId)
    },
  },

  actions: {
    //-----------------------------------------------//
    //Bootstrapping and authentication flow
    //-----------------------------------------------//
    initApp: function (context, payload) {
      return new Promise<void>((resolve, reject) => {
        //Set API comms defaults
        // axios.defaults.baseURL = LOCAL: Check the vue.config.js for parameters.
        // axios.defaults.baseURL = STANGA DEV: 'https://annum.sn77.net/api/' //Stanga QA - test creds: eugen@test.com / eugentest | jon+test01@informedfunction.com / pass
        // axios.defaults.baseURL = ANNUM STAGE: 'https://stagingapi.annumapp.com/api/' //Staging - test creds: shane@partandpixel.com / pass | jon@informedfunction.com / pass | annum@annumplanning.com / pass
        // axios.defaults.baseURL = ANNUM PROD: 'https://api.annumapp.com/api/' //Prod - test creds: ? / ?
        axios.defaults.baseURL = process.env.VUE_APP_API_BASE_URL
        axios.defaults.timeout = 100000
        axios.defaults.headers.common['Accept'] = 'application/json'
        axios.defaults.headers.common['Content-Type'] = 'application/json'

        // Flush localstorage if needed
        const lastFlushedLocalstorage =
          window.localStorage.getItem('last-flushed')
        if (lastFlushedLocalstorage) {
          const lastFlushedDate = new Date(Number(lastFlushedLocalstorage))
          const shouldFlushDate = new Date(
            context.state.shouldFlushedLocalstorage
          )
          if (shouldFlushDate > lastFlushedDate) {
            const cachedLgo = window.localStorage.getItem('lgo')
            const cachedAct = window.localStorage.getItem('act')
            window.localStorage.clear()
            window.localStorage.setItem('last-flushed', Date.now().toString())
            if (cachedLgo) {
              window.localStorage.setItem('lgo', cachedLgo)
            }
            if (cachedAct) {
              window.localStorage.setItem('act', cachedAct)
            }
          }
        } else {
          window.localStorage.setItem('last-flushed', Date.now().toString())
        }

        // Set refreshToken to maintain application state between browser refreshes in local dev environment
        if(context.state.localDevRefreshToken !== ''){
          Vue['cookie'].set('refreshToken', context.state.localDevRefreshToken)
        }

        // Check for logged in/out state
        if (
          window.localStorage.getItem('lgo') === '0' &&
          Vue['cookie'].get('refreshToken')
        ) {
          context.dispatch('refreshAccessToken').then(
            () => {
              // Load plan if plan ID is present in current route
              if(payload?.currentRoute?.params.planId){
                context.dispatch('updateCurrentPlanById', new ID(Number(payload.currentRoute.params.planId))).then(
                  ()=>{
                    context.commit('updateIsAppInitialized', true)
                    resolve()
                  }, 
                  (error)=>{
                    context.commit('updateIsAppInitialized', true)
                    reject(error)
                  }
                )
              }else{
                context.commit('updateIsAppInitialized', true)
                resolve()
              }
            },
            (error) => {
              context.commit('updateIsAppInitialized', true)
              reject(error)
            }
          )
        } else {
          window.localStorage.setItem('lgo', '1')
          context.commit('updateIsUserLoggedIn', false)
          context.commit('updateIsAppInitialized', true)
          resolve()
        }
      })
    },
    refreshAccessToken: function (context) {
      return new Promise<void>((resolve, reject) => {
        context.state.services.authentication.refreshAccessToken().then(
          (response) => {
            context.dispatch('parseInitialResponse', response).then(
              () => {
                context.commit('updateIsUserLoggedIn', true)
                context.dispatch('startRefreshAccessTokenTimer')
                resolve()
              },
              (error) => {
                context.commit('updateIsUserLoggedIn', false)
                reject(error)
              }
            )
          },
          (error) => {
            context.commit('updateIsUserLoggedIn', false)
            reject(error)
          }
        )
      })
    },
    startRefreshAccessTokenTimer: function (context) {
      context.state.refreshAccessTokenTimer = setTimeout(function () {
        context.dispatch('refreshAccessToken')
      }, 86400000)
    },
    checkLoggedInStatus: function (context) {
      const loggedInStatus =
        window.localStorage.getItem('lgo') === '0' ? true : false
      context.commit('updateIsUserLoggedIn', loggedInStatus)
      return loggedInStatus
    },
    login: function (context, userData: UserSetup) {
      return new Promise<void>((resolve, reject) => {
        context.state.services.authentication.login(userData).then(
          (initialResponseObj) => {
            context
              .dispatch('logInWithInitialResponse', initialResponseObj)
              .then(
                () => {
                  resolve()
                },
                (error) => {
                  console.warn('AppStore - login: Error from logInWithInitialResponse.')
                  reject(error)
                }
              )
          },
          (error) => {
            console.warn('AppStore - login: Error from /authenticate endpoint.')
            reject(error)
          }
        )
      })
    },
    logInWithInitialResponse: function (context, initialResponseObj) {
      return new Promise<void>((resolve, reject) => {
        context.dispatch('parseInitialResponse', initialResponseObj).then(
          () => {
            context.commit('updateIsUserLoggedIn', true)
            context.dispatch('startRefreshAccessTokenTimer')
            resolve()
          },
          (error) => {
            console.warn('AppStore - logInWithInitialResponse: Error from parseInitialResponse.', error)
            reject(error)
          }
        )
      })
    },
    logout: function (context) {
      context.state.services.authentication.logout()
      context.commit('updateIsUserLoggedIn', false)
      context.commit('updateLoggedInUser', new User())
      context.commit('updateRouteAccountIntId', 0)
      context.commit('updateCurrentPlanIntID', 0)
      Vue['cookie'].set('refreshToken', '', {expires: Date.now()})
      window.localStorage.removeItem('act')
    },
    switchAccount: function (context, accountIntId) {
      return new Promise<void>((resolve, reject) => {
        // Keep the logged-in user's profile edits when the account user objects are replaced
        const accountUser = context.getters.currentUser as User
        if (
          context.state.loggedInUser.id.intID !== 0 &&
          accountUser !== context.state.loggedInUser &&
          context.getters.allAccountUsers.includes(accountUser) &&
          accountUser.id.intID === context.state.loggedInUser.id.intID
        ) {
          syncLoggedInUserFromAccountUser(context.state.loggedInUser, accountUser)
        }

        // Request full account object
        context.getters.services.accounts.get([accountIntId]).then(
          (response) => {
            context.commit('updateCurrentAccount', response)

            // Set up user references
            context.dispatch('populateFrontEndAccountObjects')

            resolve()
          },
          (error) => {
            Vue.prototype.$toast.add(
              new AlertMessage(AlertMessageSeverity.error, error)
            )
            reject()
          }
        )
      })
    },
    parseInitialResponse: function (
      context,
      newInitialResponse: InitialResponse
    ) {
      return new Promise<void>((resolve, reject) => {
        // Set access token
        axios.defaults.headers.common[
          'Authorization'
        ] = `Bearer ${newInitialResponse.accessToken.token}`
        Vue.set(context.state, 'accessToken', newInitialResponse.accessToken)

        // Update user and roles from initial response data
        Vue.set(
          context.state,
          'currentUserIntID',
          newInitialResponse.user.id.intID
        )
        context.commit('updateLoggedInUser', newInitialResponse.user)
        Vue.set(context.state, 'allRoles', newInitialResponse.roles)

        // Populate allAccounts with account name/ID objects from logged in user
        Vue.set(context.state, 'allAccounts', newInitialResponse.user.accounts)

        //Set current date range to today's date
        context.dispatch('setAllDateRanges', { shouldFetchTactics: false })

        // Return error message if no accounts are available
        if (!newInitialResponse.user.accountIds.length) {
          console.warn('AppStore - parseInitialResponse: No accounts available for user.')
          reject('No accounts available for user.')
        }

        // Determine which account to load
        let accountIntIdToLoad: number =
          newInitialResponse.user.accountIds[0].intID
        if (
          context.getters.routeAccountIntId !== 0 &&
          newInitialResponse.user.accountIds.filter(
            (id) => id.intID === context.getters.routeAccountIntId
          ).length
        ) {
          // Set account from ID in route
          accountIntIdToLoad = context.getters.routeAccountIntId
        } else if (
          window.localStorage.getItem('act') !== '' &&
          newInitialResponse.user.accountIds.filter(
            (id) => id.intID === Number(window.localStorage.getItem('act'))
          ).length
        ) {
          // Restore previous plan selection from localstorage
          accountIntIdToLoad = Number(window.localStorage.getItem('act'))
        }

        // Request full account object
        context.dispatch('switchAccount', accountIntIdToLoad).then(
          () => {
            // Update logged in user's object in Account with rolesObserved data from initial response object
            context.getters.currentUser.rolesObserved = [...newInitialResponse.user.rolesObserved]
            resolve()
          },
          (error) => {
            console.warn('AppStore - parseInitialResponse: Error from switchAccount.')
            reject(error)
          }
        )
      })
    },

    //-----------------------------------------------//
    // Update and refresh plan data
    //-----------------------------------------------//
    updateCurrentPlan: function (context, newPlan: Plan) {
      return new Promise<void>((resolve, reject) => {
        //Update Plan object
        context.commit('updateCurrentPlan', newPlan)

        // Set date ranges if recent values exist for the plan - tactics are fetched as a result of setting date ranges
        if(context.state.recentDateRangesDictionary[context.getters.currentPlanIntID]){
          context.dispatch('restoreAllDateRangesForCurrentPlan')
        }else{
          context.dispatch('setAllDateRanges', { shouldFetchTactics: false })
        }

        //Update most recent plan on User
        context.getters.currentUser.mostRecentPlanId = newPlan.id.intID
        context.getters.services.users.update(context.getters.currentUser)

        //Reset filters for new plan
        context.dispatch('resetSelectedFilters')

        //Fetch Subscription events
        context.dispatch('fetchSubscriptionEvents').finally(() => {
          context
            .dispatch('mapDuplicateSubscriptionEvents')
            .finally(() => resolve())
        })
      })
    },
    updateCurrentPlanById: function (context, newPlanId: ID) {
      return new Promise<void>((resolve, reject) => {
        if (!context.getters.allPlans.length) {
          context
            .dispatch('switchAccount', context.getters.routeAccountIntId)
            .then(
              () => {
                context.commit('updateCurrentPlanIntID', newPlanId.intID)
                context.dispatch('refreshCurrentPlan').then((responsePlan) => {
                  if (
                    AppRouter.currentRoute.params.planId &&
                    AppRouter.currentRoute.params.planId !==
                      newPlanId.intID.toString()
                  ) {
                    AppRouter.replace({
                      params: {planId: newPlanId.intID.toString()},
                    })
                  }
                  resolve()
                })
              },
              (error) => {
                reject(
                  'AppStore - updateCurrentPlanById: Could not find Plan for ID.'
                )
              }
            )
        } else {
          const newPlan = context.getters.allPlans.find(
            (plan) => plan.id.intID == newPlanId.intID
          ) as Plan

          if (newPlan) {
            context.commit('updateCurrentPlanIntID', newPlanId.intID)
            context.dispatch('refreshCurrentPlan').then((responsePlan) => {
              if (
                AppRouter.currentRoute.params.planId &&
                AppRouter.currentRoute.params.planId !==
                  newPlan.id.intID.toString()
              ) {
                AppRouter.replace({
                  params: {planId: newPlan.id.intID.toString()},
                })
              }
              resolve()
            })
          } else {
            reject(
              'AppStore - updateCurrentPlanById: Could not find Plan for ID.'
            )
          }
        }
      })
    },
    fetchTacticsForCurrentPlanInDateRange: function (context, dateRange: Date[]){
      const requestId = ++fetchTacticsRequestId
      if(!context.getters.currentPlanIntID){
        return
      }
      let planIdsToFetch = [context.getters.currentPlanIntID]
      if(context.getters.currentPlan.isLead){
        // Fetch nested plans
        planIdsToFetch = [...context.getters.currentPlan.plans.map((plan) => plan.intID), context.getters.currentPlanIntID]
      }else if(context.getters.currentPlan.isNested){
        // Include lead plan ID if nested plan
        planIdsToFetch.push(context.getters.currentPlan.parentId.intID)
      }

      // Fetch tactics for plan IDs in date range
      context.getters.services.tactics.fetchTacticsForPlanInDateRange(planIdsToFetch, dateRange).then(
        (tacticsResponse) => {
          if (requestId !== fetchTacticsRequestId) return

          // Fetch recurring tactics for same date range
          context.getters.services.tactics.fetchTacticsForPlanInDateRange(planIdsToFetch, dateRange, true).then(
            (recurringTacticsResponse) => {
              
              // Fetch initiatives for same date range
              context.getters.services.initiatives.fetchInitiativesForPlanInDateRange(planIdsToFetch, dateRange).then(
                (initiativesResponse) => {
                  // PROCESS TACTICS
                  // Combine response IDs for filtering existing tactics
                  const responseIds = new Set([
                    ...tacticsResponse.map((t: Tactic) => t.id.intID),
                    ...recurringTacticsResponse.map((t: Tactic) => t.id.intID)
                  ])

                  // Keep existing tactics that are not in the response
                  const keptTactics = context.getters.currentPlan.tactics.filter((t: Tactic) => !responseIds.has(t.id.intID))

                  // Combine kept tactics with response tactics (responses take precedence)
                  context.getters.currentPlan.tactics = [...keptTactics, ...tacticsResponse, ...recurringTacticsResponse]

                  // PROCESS INITIATIVES
                  // Capture currently selected filter IDs before updating initiatives array
                  const previouslySelectedInitiativeIds = new Set(context.state.selectedInitiativesIntIds)
                  const previouslySelectedCustomizationIds = new Set(context.state.selectedInitiativeCustomizationsIntIds)

                  // Merge initiatives: keep existing ones not in response, add/update from response
                  const responseInitiativeIds = new Set(initiativesResponse.map((i: Initiative) => i.id.intID))
                  const keptInitiatives = context.getters.currentPlan.initiatives.filter((i: Initiative) => !responseInitiativeIds.has(i.id.intID))
                  context.getters.currentPlan.initiatives = [...keptInitiatives, ...initiativesResponse]

                  // Manage initiative filters based on new data
                  if(context.getters.isInitiativeFilterActive){
                    // "No Initiatives linked" mode: selected IDs are intentionally empty,
                    // so skip the "no valid initiatives" check to avoid deactivating the filter.
                    if(!context.getters.filterNoInitiative) {
                      // Filter is active: keep only selected initiatives that still exist in new data
                      const validSelectedInitiatives = context.getters.currentInitiatives.filter(
                        (initiative: Initiative) => previouslySelectedInitiativeIds.has(initiative.id.intID)
                      )

                      // Keep only selected customizations that still exist in new data
                      const validSelectedCustomizations = context.getters.currentInitiativeCustomizations.filter(
                        (customization: InitiativeCustomization) => previouslySelectedCustomizationIds.has(customization.id.intID)
                      )

                      // If no valid filters remain, deactivate the filter
                      if(validSelectedInitiatives.length === 0 && validSelectedCustomizations.length === 0){
                        context.commit('updateSelectedInitiatives', context.getters.currentInitiatives)
                        context.commit('updateSelectedInitiativeCustomizations', context.getters.currentInitiativeCustomizations)
                        context.commit('isInitiativeFilterActive', false)
                      }else{
                        if(validSelectedInitiatives.length){
                          context.commit('updateSelectedInitiatives', validSelectedInitiatives)
                        }
                        if(validSelectedCustomizations.length){
                          context.commit('updateSelectedInitiativeCustomizations', validSelectedCustomizations)
                        }
                        context.commit('isInitiativeFilterActive', true)
                      }
                    }
                    // else: filterNoInitiative=true — keep isInitiativeFilterActive=true as-is
                  }else{
                    // Filter is not active: reset to include all initiatives and customizations
                    context.commit('updateSelectedInitiatives', context.getters.currentInitiatives)
                    context.commit('updateSelectedInitiativeCustomizations', context.getters.currentInitiativeCustomizations)
                  }

                  // PROCESS FRONT-END OBJECTS
                  // Update associations between tactics, initiatives, and other front-end plan objects
                  context.dispatch('populateFrontEndPlanObjects')
                },
                (error) => {
                  console.warn('AppStore - fetchInitiativesForCurrentPlanInDateRange: Error fetching initiatives for current plan in date range.', error)
                }
              )
            },
            (error) => {
              console.warn('AppStore - fetchTacticsForCurrentPlanInDateRange: Error fetching recurring tactics with fetchTacticsForPlanInDateRange.', error)
            }
          )
        },
        (error) => {
          if (requestId !== fetchTacticsRequestId) return
          console.warn('AppStore - fetchTacticsForCurrentPlanInDateRange: Error fetching non-recurring tactics with fetchTacticsForPlanInDateRange.', error)
        }
      )
    },
    fetchTacticsRelatedToInitiative: function (context, initiativeId: ID) {
      return new Promise<void>((resolve, reject) => {
        context.getters.services.tactics.fetchTacticsRelatedToInitiative(initiativeId).then(
          (response) => {
            // Add related tactics to current plan tactics array if not already present
            const currentTacticIntIds = context.getters.currentPlan.tactics.map((tactic) => tactic.id.intID)
            const newTactics = response.filter((tactic) => !currentTacticIntIds.includes(tactic.id.intID))
            context.getters.currentPlan.tactics.push(...newTactics)

            // Update associations with tactic objects
            context.dispatch('populateFrontEndPlanObjects')

            resolve()
          },
          (error) => {
            console.warn('AppStore - fetchTacticsRelatedToInitiative: Error fetching tactics related to initiative.', error)
            reject(error)
          }
        )
      })
    },
    searchTactics: function (context, payload: { keyword: string, startDate: Date, endDate: Date }) {
      return new Promise<Tactic[]>((resolve, reject) => {
        const searchService = context.getters.services.tactics
        const planId = context.getters.currentPlanIntID

        // Fire both requests in parallel; allSettled ensures processing runs even if one fails
        Promise.allSettled([
          searchService.search(planId, payload.keyword, payload.startDate, payload.endDate, false),
          searchService.search(planId, payload.keyword, payload.startDate, payload.endDate, true),
        ]).then(([nonRecurringResult, recurringResult]) => {
          if (nonRecurringResult.status === 'rejected') {
            console.warn('AppStore - searchTactics: Non-recurring search failed.', nonRecurringResult.reason)
          }
          if (recurringResult.status === 'rejected') {
            console.warn('AppStore - searchTactics: Recurring search failed.', recurringResult.reason)
          }

          // If both failed, reject
          if (nonRecurringResult.status === 'rejected' && recurringResult.status === 'rejected') {
            reject(nonRecurringResult.reason)
            return
          }

          const nonRecurringTactics: Tactic[] = nonRecurringResult.status === 'fulfilled' ? nonRecurringResult.value as Tactic[] : []
          const recurringTactics: Tactic[] = recurringResult.status === 'fulfilled' ? recurringResult.value as Tactic[] : []
          const allTactics = [...nonRecurringTactics, ...recurringTactics]

          // Add base results to current plan tactics array if not already present
          const currentTacticIntIds = context.getters.currentPlan.tactics.map((tactic: Tactic) => tactic.id.intID)
          const newTactics = allTactics.filter((tactic: Tactic) => !currentTacticIntIds.includes(tactic.id.intID))
          context.getters.currentPlan.tactics.push(...newTactics)

          // Populate front-end fields (channelName, tacticTypeName, tags, etc.) on base
          // tactics first so that clones inherit populated values via spread below
          context.dispatch('populateFrontEndPlanObjects')

          // Generate recurrence clones for recurring tactics within the search date range,
          // mirroring TacticType.getTacticsWithClonesForDateRange(). Clones are returned
          // directly rather than being added to currentPlan.tactics, to avoid polluting
          // plan state with synthetic IDs.
          const timezoneOffset = new Date().getTimezoneOffset()
          const adjustedStartDate = new Date(payload.startDate)
          adjustedStartDate.setDate(adjustedStartDate.getDate() - 1)
          const adjustedEndDate = new Date(payload.endDate)
          adjustedEndDate.setDate(adjustedEndDate.getDate() + 1)

          const clones: Tactic[] = []
          recurringTactics.forEach((tactic: Tactic) => {
            // Spread from the plan's copy so populated fields (channelName, tacticTypeName,
            // tags, etc.) are inherited — populateFrontEndPlanObjects mutates plan objects
            // in place, so plan copy is authoritative after the dispatch above
            const populatedTactic = (context.getters.currentPlan.tactics as Tactic[]).find(
              (t) => t.id.intID === tactic.id.intID
            ) ?? tactic

            const diff = differenceInMinutes(populatedTactic.endDate, populatedTactic.startDate)
            const rules = rrulestr(populatedTactic.rrule as string)
            const thisRecurrenceEndDate = populatedTactic.recurrenceEndDate ?? new Date(new Date().getFullYear() + 1000, 0, 1)

            rules
              .between(adjustedStartDate, adjustedEndDate)
              .filter((date) => date > populatedTactic.startDate && date < thisRecurrenceEndDate)
              .forEach((recurrenceDate) => {
                recurrenceDate.setHours(recurrenceDate.getHours() + (timezoneOffset / 60))
                clones.push({
                  ...populatedTactic,
                  id: new ID(Number(populatedTactic.id.intID.toString() + recurrenceDate.getTime().toString())),
                  startDate: recurrenceDate,
                  endDate: addMinutes(recurrenceDate, diff),
                  isRecurringEventClone: true,
                  originalTacticID: populatedTactic.id.clone(),
                } as Tactic)
              })
          })

          // Swap API-fresh objects for their populated plan copies so channelName,
          // tacticTypeName, tags, etc. are present in the returned results.
          // Recurring base tactics are filtered to only those whose own dates fall within
          // the search range — instances outside the range are represented by clones only.
          const populatedTactics = allTactics
            .filter((tactic: Tactic) => {
              if (tactic.rrule) {
                return tactic.startDate <= payload.endDate && tactic.endDate >= payload.startDate
              }
              return true
            })
            .map((tactic: Tactic) =>
              (context.getters.currentPlan.tactics as Tactic[]).find(
                (t) => t.id.intID === tactic.id.intID
              ) ?? tactic
            )

          resolve([...populatedTactics, ...clones])
        })
      })
    },
    fetchInitiativesForCurrentPlanInDateRange: function (context, dateRange: Date[]) {
      // This is used for fetching initiatives on their own, generally for the PlanSettingsInitiaitves view
      // Initiatives are also fetched when fetching tactics for a plan in a date range in fetchTacticsForCurrentPlanInDateRange
      let planIdsToFetch = [context.getters.currentPlanIntID]
      if(context.getters.currentPlan.isLead){
        // Fetch nested plans
        planIdsToFetch = [...context.getters.currentPlan.plans.map((plan) => plan.intID), context.getters.currentPlanIntID]
      }
      if(context.getters.currentPlan.isNested){
        // Include lead plan ID if nested plan
        planIdsToFetch.push(context.getters.currentPlan.parentId.intID)
      }

      return new Promise<void>((resolve, reject) => {
        // Fetch initiatives for plan IDs in date range
        context.getters.services.initiatives.fetchInitiativesForPlanInDateRange(planIdsToFetch, dateRange).then(
          (response) => {
            // Overwrite initiatives for current plan
            context.getters.currentPlan.initiatives = response

            // Update associations with initiative objects
            context.dispatch('populateFrontEndPlanObjects')

            resolve()
          },
          (error) => {
            console.warn('AppStore - fetchInitiativesForCurrentPlanInDateRange: Error fetching initiatives for current plan in date range.', error)
            reject(error)
          }
        )
      })
    },
    // TODO: 
    fetchUnscheduledTacticsForCurrentPlan: function (context) {
      let planIdsToFetch = [context.getters.currentPlanIntID]
      if(context.getters.currentPlan.isLead){
        // Fetch nested plans
        planIdsToFetch = [...context.getters.currentPlan.plans.map((plan) => plan.intID), context.getters.currentPlanIntID]
      }else if(context.getters.currentPlan.isNested){
        // Include lead plan ID if nested plan
        planIdsToFetch.push(context.getters.currentPlan.parentId.intID)
      }

      context.getters.services.tactics.fetchUnscheduledTacticsForPlanIds(planIdsToFetch).then(
        (unscheduledTacticsResponse) => {
          // Prepare response IDs for filtering existing tactics
          const responseIds = new Set([...unscheduledTacticsResponse.map((t: Tactic) => t.id.intID)])

          // Keep existing tactics that are not in the response
          const keptTactics = context.getters.currentPlan.tactics.filter((t: Tactic) => !responseIds.has(t.id.intID))

          // Combine kept tactics with response tactics (responses take precedence)
          context.getters.currentPlan.tactics = [...keptTactics, ...unscheduledTacticsResponse]

          // Update associations with tactic objects
          context.dispatch('populateFrontEndPlanObjects')
        },
        (error) => {
          console.warn('AppStore - fetchTacticsForCurrentPlanInDateRange: Error fetching recurring tactics with fetchTacticsForPlanInDateRange.', error)
        }
      )
    },
    resetSelectedFilters: function (context) {
      return new Promise<void>((resolve, reject) => {
        context.commit('updateIsLeadPlanVisible', true)
        if (context.getters.currentPlan.plans.length) {
          context.commit('updateVisibleNestedPlansIntIds', context.getters.currentPlan.plans.map((planId) => planId.intID))
        } else if (context.getters.currentPlan.parentId.intID !== 0) {
          context.commit('updateVisibleNestedPlansIntIds', [context.getters.currentPlan.id.intID,])
        }
        context.commit('isNestedPlanFilterActive', false)

        context.commit('updateSelectedTacticTypes', context.getters.currentTacticTypes)
        context.commit('isChannelFilterActive', false)
        context.commit('updateSelectedTags', context.getters.currentTags)
        context.commit('isTagFilterActive', false)
        context.commit('updateSelectedSubscriptions', [])
        context.commit('isSubscriptionFilterActive', false)
        context.commit('updateSelectedInitiatives', context.getters.currentInitiatives)
        context.commit('updateSelectedInitiativeCustomizations', context.getters.currentInitiativeCustomizations)
        context.commit('isInitiativeFilterActive', false)
        context.dispatch('setSelectedPlanPropertiesFromLocalStorage')
        resolve()
      })
    },
    refreshCurrentPlan: function (context, openedChannels: number[] = [], planToUse: Plan = {} as Plan) {
      //TODO: This is in place because Plan is the only API that returns whole objects for Channels, Categories, Types, and Fields. Consider revising this system with API and/or UI changes to be more efficient in the future.
      const planToRefreshIntID: number = planToUse.id ? planToUse.id.intID : context.getters.currentPlan.id.intID
      const planToRefresh: Plan = context.getters.allPlans.find((plan) => plan.id.intID == planToRefreshIntID) as Plan

      return new Promise((resolve, reject) => {
        context.state.services.plans.get([planToRefreshIntID]).then(
          async (returnedPlan) => {
            const updatedPlan: Plan = returnedPlan as Plan

            if(!planToRefresh || !updatedPlan){
              reject(`AppStore - refreshCurrentPlan: Could not find Plan for ID: planToRefresh: ${planToRefresh} / updatedPlan: ${updatedPlan}`)
            }

            // Update basic properties (excluding ID)
            planToRefresh.accountId = updatedPlan.accountId
            planToRefresh.name = updatedPlan.name
            planToRefresh.type = updatedPlan.type
            planToRefresh.startDate = updatedPlan.startDate
            planToRefresh.endDate = updatedPlan.endDate
            planToRefresh.weekStartDay = updatedPlan.weekStartDay
            planToRefresh.ratingPointsType = updatedPlan.ratingPointsType
            planToRefresh.isTemplate = updatedPlan.isTemplate
            planToRefresh.useBudgets = updatedPlan.useBudgets
            planToRefresh.defaultChannels = updatedPlan.defaultChannels
            planToRefresh.isFrozen = updatedPlan.isFrozen
            planToRefresh.acronym = updatedPlan.acronym

            // update connected object IDs
            planToRefresh.userRoleIds = updatedPlan.userRoleIds
            planToRefresh.budgetIds = updatedPlan.budgetIds
            planToRefresh.subscriptionIds = updatedPlan.subscriptionIds
            planToRefresh.tags = updatedPlan.tags

            // Update lead/nested properties
            planToRefresh.plans = updatedPlan.plans
            planToRefresh.parentId = updatedPlan.parentId

            // Update channels and fields
            planToRefresh.channels = updatedPlan.channels
            planToRefresh.tacticFields = updatedPlan.tacticFields
            if (openedChannels.length) {
              planToRefresh.channels.forEach((channel) => {
                if (openedChannels.includes(channel.id.intID)) {
                  channel.opened = true
                }
              })
            }
        
            // Update documents
            planToRefresh.planDocumentCategories = updatedPlan.planDocumentCategories
            planToRefresh.documents = updatedPlan.documents
        
            // Update dashboards
            planToRefresh.useDashboards = updatedPlan.useDashboards
            planToRefresh.planDashboardCategories = updatedPlan.planDashboardCategories
            planToRefresh.dashboards = updatedPlan.dashboards
        
            // Update initiatives
            planToRefresh.initiativeChannel = updatedPlan.initiativeChannel
            planToRefresh.initiatives = updatedPlan.initiatives
        
            // Update userRoles
            planToRefresh.userRoles = context.getters.currentAccount.userRoles.filter(
              (accountUserRole) => 
                planToRefresh.userRoleIds.filter(
                  (planUserRoleId) => planUserRoleId.intID === accountUserRole.id.intID
                ).length ? true : false
            )

            // Update plan in local store using updateCurrentPlan, which triggers fetch of tactics for current date range(s)
            context
              .dispatch('updateCurrentPlan', planToRefresh)
              .finally(() => {
                // Update budgets
                if(planToRefresh.budgetIds.length){
                  context.getters.services.budgets.get(planToRefresh.budgetIds.map((id)=>id.intID)).then(
                    (budgets)=>{
                      planToRefresh.budgets = budgets
                      resolve(planToRefresh)
                    }, 
                    (error)=>{
                      reject(`AppStore - refreshCurrentPlan: Error fetching budgets for plan. ${error}`)
                    }
                  )
                }else{
                  resolve(planToRefresh)
                }
              })
          },
          (error) => {
            Vue.prototype.$toast.add(
              new AlertMessage(AlertMessageSeverity.error, error)
            )
            reject(error)
          }
        )
      })
    },
    refreshCurrentAccount: function (context) {
      return new Promise((resolve, reject) => {
        context.state.services.accounts
          .get(context.getters.currentAccount.id.intID)
          .then(
            (returnedAccount) => {
              context.commit('updateCurrentAccount', returnedAccount)
              
              // Set up user references
              context.dispatch('populateFrontEndAccountObjects')
              
              resolve(returnedAccount)
            },
            (error) => {
              Vue.prototype.$toast.add(
                new AlertMessage(AlertMessageSeverity.error, error)
              )
              reject(error)
            }
          )
      })
    },
    refreshCurrentTactic: function (context, updatedTactic?: Tactic) {
      return new Promise((resolve, reject) => {
        if (updatedTactic) {
          const indexOfCurrentTactic =
            context.getters.currentPlan.tactics.findIndex(
              (tactic) => tactic.id.intID == context.getters.currentTacticIntID
            )
          context.getters.currentPlan.tactics.splice(
            indexOfCurrentTactic,
            1,
            updatedTactic
          )
          context.dispatch('populateFrontEndPlanObjects')
          resolve(context.getters.currentPlan.tactics[indexOfCurrentTactic])
        } else {
          context.state.services.tactics
            .get([context.getters.currentTacticIntID])
            .then(
              (returnedTactic) => {
                const indexOfCurrentTactic =
                  context.getters.currentPlan.tactics.findIndex(
                    (tactic) =>
                      tactic.id.intID == context.getters.currentTacticIntID
                  )
                context.getters.currentPlan.tactics.splice(
                  indexOfCurrentTactic,
                  1,
                  returnedTactic
                )
                context.dispatch('populateFrontEndPlanObjects')
                resolve(
                  context.getters.currentPlan.tactics[indexOfCurrentTactic]
                )
              },
              (error) => {
                Vue.prototype.$toast.add(
                  new AlertMessage(AlertMessageSeverity.error, error)
                )
                reject(error)
              }
            )
        }
      })
    },
    addTacticToCurrentPlan: function (context, newTactic: Tactic) {
      return new Promise<void>((resolve, reject) => {
        context.getters.currentPlan.tactics.push(newTactic)
        context.dispatch('populateFrontEndPlanObjects')
        resolve()
      })
    },
    removeTacticFromCurrentPlanById: function (context, tacticId: ID) {
      return new Promise<void>((resolve, reject) => {
        let found = false

        context.getters.allPlans.forEach((plan: Plan) => {
          const indexOfTactic = plan.tactics.findIndex(
            (tactic) => tactic.id.intID == tacticId.intID
          )
          if (indexOfTactic !== -1) {
            plan.tactics.splice(indexOfTactic, 1)
            found = true
          }
        })

        if (found) {
          context.dispatch('populateFrontEndPlanObjects')
          resolve()
        } else {
          reject('Could not find tactic for ID.')
        }
      })
    },
    // Replace an existing tactic (matched by id, across all loaded plans) with an updated copy.
    // Generic counterpart to refreshCurrentTactic for callers that aren't the current tactic
    // (e.g. recurrence-break operations from the calendar). Resolves false if not found.
    updateTacticInCurrentPlan: function (context, updatedTactic: Tactic) {
      return new Promise<boolean>((resolve) => {
        let found = false

        context.getters.allPlans.forEach((plan: Plan) => {
          const indexOfTactic = plan.tactics.findIndex(
            (tactic) => tactic.id.intID == updatedTactic.id.intID
          )
          if (indexOfTactic !== -1) {
            plan.tactics.splice(indexOfTactic, 1, updatedTactic)
            found = true
          }
        })

        if (found) {
          context.dispatch('populateFrontEndPlanObjects')
        }
        resolve(found)
      })
    },
    addInitiativeToCurrentPlan: function (context, newInitiative: Initiative) {
      return new Promise<void>((resolve, reject) => {
        context.getters.currentPlan.initiatives.push(newInitiative)
        context.dispatch('populateFrontEndPlanObjects')
        resolve()
      })
    },
    removeInitiativeFromCurrentPlanById: function (context, initiativeId: ID) {
      return new Promise<void>((resolve, reject) => {
        const indexOfInitiative = context.getters.currentPlan.initiatives.findIndex((initiative) => initiative.id.intID == initiativeId.intID)
        if (indexOfInitiative >= 0) {
          context.getters.currentPlan.initiatives.splice(indexOfInitiative, 1)
          context.dispatch('populateFrontEndPlanObjects')
          resolve()
        } else {
          reject('Could not find initiative for ID.')
        }
      })
    },
    addInitiativeCustomizationToCurrentPlan: function (context, newInitiativeCustomization: InitiativeCustomization) {
      return new Promise<void>((resolve, reject) => {
        const parentInitiative = context.getters.currentPlan.initiatives.find((initiative) => initiative.id.intID === newInitiativeCustomization.initiativeId.intID)
        if(parentInitiative){
          parentInitiative.initiativeCustomizations.push(newInitiativeCustomization)

          // Update selected customizations so the new customization appears immediately
          context.commit('updateSelectedInitiativeCustomizations', [...context.getters.selectedInitiativeCustomizations, newInitiativeCustomization])

          context.dispatch('populateFrontEndPlanObjects')
          resolve()
        }else{
          reject()
        }
      })
    },
    updateInitiativeInCurrentPlan: function (context, updatedInitiative: Initiative) {
      return new Promise<void>((resolve, reject) => {
        const initiativeIndex = context.getters.currentPlan.initiatives.findIndex(
          (initiative: Initiative) => initiative.id.intID === updatedInitiative.id.intID
        )
        if(initiativeIndex !== -1){
          // Preserve existing customizations since API response may not include them
          const existingCustomizations = context.getters.currentPlan.initiatives[initiativeIndex].initiativeCustomizations
          updatedInitiative.initiativeCustomizations = existingCustomizations

          // Replace the initiative in the plan's array
          Vue.set(context.getters.currentPlan.initiatives, initiativeIndex, updatedInitiative)

          // Update selected initiatives if this initiative is currently selected
          const selectedInitiatives = context.getters.selectedInitiatives
          const selectedIndex = selectedInitiatives.findIndex(
            (initiative: Initiative) => initiative.id.intID === updatedInitiative.id.intID
          )
          if(selectedIndex !== -1){
            const updatedSelectedInitiatives = [...selectedInitiatives]
            updatedSelectedInitiatives[selectedIndex] = updatedInitiative
            context.commit('updateSelectedInitiatives', updatedSelectedInitiatives)
          }

          context.dispatch('populateFrontEndPlanObjects')
          resolve()
        }else{
          reject()
        }
      })
    },
    updateInitiativeCustomizationInCurrentPlan: function (context, updatedCustomization: InitiativeCustomization) {
      return new Promise<void>((resolve, reject) => {
        const parentInitiative = context.getters.currentPlan.initiatives.find((initiative) => initiative.id.intID === updatedCustomization.initiativeId.intID)
        if(parentInitiative){
          // Find and replace the customization in the parent initiative's array
          const customizationIndex = parentInitiative.initiativeCustomizations.findIndex(
            (customization: InitiativeCustomization) => customization.id.intID === updatedCustomization.id.intID
          )
          if(customizationIndex !== -1){
            Vue.set(parentInitiative.initiativeCustomizations, customizationIndex, updatedCustomization)
          }

          // Update selected customizations if this customization is currently selected
          const selectedCustomizations = context.getters.selectedInitiativeCustomizations
          const selectedIndex = selectedCustomizations.findIndex(
            (customization: InitiativeCustomization) => customization.id.intID === updatedCustomization.id.intID
          )
          if(selectedIndex !== -1){
            const updatedSelectedCustomizations = [...selectedCustomizations]
            updatedSelectedCustomizations[selectedIndex] = updatedCustomization
            context.commit('updateSelectedInitiativeCustomizations', updatedSelectedCustomizations)
          }

          context.dispatch('populateFrontEndPlanObjects')
          resolve()
        }else{
          reject()
        }
      })
    },
    populateFrontEndPlanObjects: function (context) {
      return new Promise<void>((resolve, reject) => {
        // Manage hidden tags
        if(context.getters.currentUser.blockedTagIds.length){
          // Remove tactics and initiatives associated with hidden tags
          context.getters.currentPlan.tactics = context.getters.currentPlan.tactics.filter((tactic) => tactic.tagIds.every((tacticTagId) => !context.getters.currentUser.blockedTagIds.map((id) => id.intID).includes(tacticTagId.intID)))

          // Remove hidden tags
          context.getters.currentPlan.tags = context.getters.currentPlan.tags.filter((tag) => !context.getters.currentUser.blockedTagIds.map((id) => id.intID).includes(tag.id.intID))
        }

        // Remove tags from currentPlan that belong to sibling nested plans
        if(context.getters.currentPlan.parentId.intID !== 0){
          context.getters.currentPlan.tags = context.getters.currentPlan.tags.filter((tag) => tag.planId.intID === context.getters.currentPlan.id.intID || tag.planId.intID === context.getters.currentPlan.parentId.intID)
        }

        // Add Initiative instance references on Plan to InitiativeType instances
        const initiativeChannel = context.getters.currentPlan.initiativeChannel
        initiativeChannel.sortedInitiativeTypes?.forEach((type)=>{
          type.initiativeChannelId = initiativeChannel.id.clone()

          // Set abbreviatedPlanName on nested channels
          if (initiativeChannel.isNested && context.getters.currentPlan.abbreviatedName !== '') {
            type.abbreviatedPlanName = context.getters.currentPlan.abbreviatedName
          }

          // Add initiatives to type, fill properties on initiative instances
          type.initiatives = context.getters.currentPlan.initiatives.filter((initiative) => {
            if(initiative.initiativeType.id.intID === type.id.intID){
              const planForInitiative = context.getters.allPlans.find((plan) => plan.id.intID == initiative.planId.intID)
              initiative.initiativeTypeName = type.name
              initiative.abbreviatedPlanName = initiative.isNested ? planForInitiative.abbreviatedName : ''
              initiative.tags = context.getters.currentPlan.tags.filter((tag) => initiative.tagIds.map((id) => id.intID).includes(tag.id.intID))
              initiative.documents = context.getters.currentPlan.documents.filter((document) => initiative.documentIds.map((id) => id.intID).includes(document.id.intID))
              
              // Fill properties on InitiativeCustomization instances
              initiative.initiativeCustomizations?.forEach((customization) => {
                const planForCustomization = context.getters.allPlans.find((plan) => plan.id.intID == customization.planId.intID)
                customization.title = initiative.title
                customization.initiativeTypeName = type.name
                customization.abbreviatedPlanName = planForCustomization?.abbreviatedName
                customization.fullPlanName = `${customization.abbreviatedPlanName} - ${planForCustomization?.name}`
                customization.leadInitiativeSummaryContentUrl = initiative.initiativeSummary?.contentUrl ?? null

                // Merge tags from parent initiative and customization (using IDs to build fresh array each time)
                const initiativeTagIds = initiative.tagIds.map((id: ID) => id.intID)
                const customizationTagIds = customization.tagIds.map((id: ID) => id.intID)
                const mergedTagIds = new Set<number>([...initiativeTagIds, ...customizationTagIds])
                customization.tags = context.getters.currentPlan.tags.filter((tag: Tag) => mergedTagIds.has(tag.id.intID))

                // Merge documents from parent initiative and customization (using IDs to build fresh array each time)
                const initiativeDocumentIds = initiative.documentIds.map((id: ID) => id.intID)
                const customizationDocumentIds = customization.documentIds.map((id: ID) => id.intID)
                const mergedDocumentIds = new Set<number>([...initiativeDocumentIds, ...customizationDocumentIds])
                customization.documents = context.getters.currentPlan.documents.filter((document: PlanDocument) => mergedDocumentIds.has(document.id.intID))
              })
              
              return initiative
            }
          })
        })

        //Add Tactic instance references on Plan to TacticType instances
        context.getters.currentPlan.channels.forEach((channel) => {
          const planForThisChannel =
            context.getters.currentPlan.id.intID !== channel.planId.intID
              ? context.getters.allPlans.find(
                  (plan) => plan.id.intID == channel.planId.intID
                )
              : null
          const abbreviatedPlanNameFromChannel = planForThisChannel
            ? planForThisChannel.abbreviatedName
            : ''
          if (channel.isNested && abbreviatedPlanNameFromChannel !== '') {
            channel.abbreviatedPlanName = abbreviatedPlanNameFromChannel
          }
          channel.tacticTypes.forEach((type) => {
            if (channel.isNested && abbreviatedPlanNameFromChannel !== '') {
              type.abbreviatedPlanName = abbreviatedPlanNameFromChannel
            }

            // Populate names on Type instances
            type.channelName = channel.name

            type.tactics = context.getters.currentPlan.tactics.filter(
              (tactic) => {
                if (tactic.tacticTypeId.intID == type.id.intID) {
                  // Ensure planId is set correctly on Tactics
                  if(!tactic.isNested && !tactic.isLead){
                    tactic.planId = context.getters.currentPlan.id.clone()
                  }

                  // Populate names on Tactic instances
                  tactic.channelName = channel.name
                  tactic.tacticTypeName = type.name

                  // Populate objects from Plan on Tactic instances
                  tactic.tags = context.getters.currentPlan.tags.filter((tag)=>tactic.tagIds.map((id)=>id.intID).includes(tag.id.intID))
                  tactic.tacticPlatforms = context.getters.currentTacticPlatforms.filter((tacticPlatform)=>tactic.tacticPlatformIds.map((id)=>id.intID).includes(tacticPlatform.id.intID))
                  tactic.documents = context.getters.currentPlan.documents.filter((document)=>tactic.documentIds.map((id)=>id.intID).includes(document.id.intID))

                  if (tactic.isNested) {
                    // Set abbreviated plan name
                    if (abbreviatedPlanNameFromChannel !== '') {
                      tactic.abbreviatedPlanName = abbreviatedPlanNameFromChannel
                    } else {
                      tactic.abbreviatedPlanName = context.getters.allPlans.find((plan) => plan.id.intID == tactic.planId.intID)?.abbreviatedName
                    }

                    // Update related initiative mappings if a customization exists for the nested plan
                    tactic.relatedInitiatives?.forEach((mapping) => {
                      const thisInitiative = context.getters.currentInitiatives.find((initiative) => initiative.id.intID === mapping.initiativeId.intID)
                      if(thisInitiative && thisInitiative.initiativeCustomizations.length){
                        const thisCustomization = thisInitiative.initiativeCustomizations.find((customization) => customization.planId.intID === context.getters.currentPlanIntID)
                        if(thisCustomization){
                          mapping.initiativeId = thisCustomization.id
                          mapping.initiativeTitle = thisCustomization.title
                          mapping.initiativeIdForCustomization = thisInitiative.id
                        }
                      }
                    })
                  }

                  return tactic
                }
              }
            )
          })
        })
        // Add abbreviated plan names to tags
        context.getters.currentPlan.tags.forEach((tag) => {
          const planForThisTag =
            context.getters.currentPlan.id.intID !== tag.planId.intID
              ? context.getters.allPlans.find(
                  (plan) => plan.id.intID == tag.planId.intID
                )
              : null
          const abbreviatedPlanNameFromTag = planForThisTag
            ? planForThisTag.abbreviatedName
            : ''
          if (tag.isNested && abbreviatedPlanNameFromTag !== '') {
            tag.abbreviatedPlanName = abbreviatedPlanNameFromTag
          }
        })
        // Add dashboards to categories
        context.getters.currentPlan.planDashboardCategories.forEach((category) => {
          category.dashboards = context.getters.currentPlan.dashboards
            .filter((dashboard) => dashboard.category.id.intID === category.id.intID)
            .map((dashboard) => {
              dashboard.plan = context.getters.currentPlan.id.clone()
              return dashboard
            })
        })
        resolve()
      })
    },
    populateFrontEndAccountObjects: function(context) {
      // Add Role references to UserRole objects on currentAccount
      context.getters.currentAccount.userRoles.forEach((userRole) => {
        userRole.role = context.getters.allRoles.find((role) => role.id.intID === userRole.roleId.intID)
      })

      context.getters.currentAccount.plans.forEach((accountPlan) => {
        if(accountPlan.type === PlanType.Master){
          const nestedPlansForThisLeadPlan = context.getters.currentAccount.plans.filter((checkPlan) => checkPlan.parentId?.intID === accountPlan.id.intID)
          
          // Add nested plan IDs to plans on account object based on parentId values from lead plans
          accountPlan.plans = nestedPlansForThisLeadPlan.map((plan) => plan.id)

          // Add lead tags to nested plans
          nestedPlansForThisLeadPlan.forEach((plan) => {
            plan.tags = [...plan.tags, ...accountPlan.tags.filter((tag) => tag.isLead === true)]
          })
        }
      })

      // Add abbreviated plan names to tags
      context.getters.currentAccount.plans.forEach((plan) => {
        plan.tags.forEach((tag) => {
          const planForThisTag =
            context.getters.currentPlan.id.intID !== tag.planId.intID
              ? context.getters.allPlans.find(
                  (plan) => plan.id.intID == tag.planId.intID
                )
              : null
          const abbreviatedPlanNameFromTag = planForThisTag
            ? planForThisTag.abbreviatedName
            : ''
          if (tag.isNested && abbreviatedPlanNameFromTag !== '') {
            tag.abbreviatedPlanName = abbreviatedPlanNameFromTag
          }
        })
      })
    },
    updateMostRecentCoreView: function (context, viewName: UserMostRecentView) {
      // Removed references to this action to prevent excessive API calls - JV 03.17.23
      return new Promise<void>((resolve, reject) => {
        context.getters.currentUser.mostRecentView = viewName
        context.getters.services.users.update(context.getters.currentUser)
        resolve()
      })
    },
    fetchSubscriptionEvents: function (context) {
      let processedSubscriptions = 0
      return new Promise<void>((resolve, reject) => {
        if (!context.getters.currentSubscriptions.length) {
          resolve()
        } else {
          context.getters.currentSubscriptions.forEach(
            async (subscription, subscriptionIndex) => {
              if (
                !subscription.events.length ||
                Date.now() - subscription.lastUpdate.getTime() > 86400000
              ) {
                await context.state.services.accounts
                  .getSubscriptionEvents(subscription)
                  .then(
                    (response) => {
                      subscription.lastUpdate = new Date()
                      subscription.events = response as Tactic[]

                      subscription.events.forEach(
                        (subscriptionEvent, eventIndex) => {
                          const thisIntId = Number(
                            subscriptionIndex.toString() + eventIndex.toString()
                          )
                          subscriptionEvent.id = new ID(
                            thisIntId,
                            '/' + subscription.name + '/' + thisIntId.toString()
                          )
                          subscriptionEvent.channelName = subscription.name
                          subscriptionEvent.isSubscriptionEvent = true

                          // Account for timezone offset
                          subscriptionEvent.startDate = new Date(subscriptionEvent.startDate.getTime() + (subscriptionEvent.startDate.getTimezoneOffset() * 60000));
                          subscriptionEvent.endDate = new Date(subscriptionEvent.endDate.getTime() + (subscriptionEvent.endDate.getTimezoneOffset() * 60000));

                          // Should event be allDay?
                          if (
                            subscriptionEvent.startDate &&
                            subscriptionEvent.endDate &&
                            subscriptionEvent.endDate -
                              subscriptionEvent.startDate >=
                              86400000 &&
                            subscriptionEvent.startDate.getUTCHours() == 0 &&
                            subscriptionEvent.endDate.getUTCHours() == 0
                          ) {
                            subscriptionEvent.isAllDay = true
                            subscriptionEvent.startDate.setDate(
                              subscriptionEvent.startDate.getDate() + 1
                            )
                          }
                          // Set DTSTART for subscription events that don't have one
                          if (subscriptionEvent.rrule && subscriptionEvent.rrule?.indexOf('DTSTART=') < 0) {
                            const year = subscriptionEvent.startDate.getFullYear()
                            const month =
                              subscriptionEvent.startDate.getMonth() > 8
                                ? subscriptionEvent.startDate.getMonth() + 1
                                : '0' + (subscriptionEvent.startDate.getMonth() + 1)
                            const date =
                              subscriptionEvent.startDate.getDate() > 9
                                ? subscriptionEvent.startDate.getDate()
                                : '0' + subscriptionEvent.startDate.getDate()
                            const hours =
                              subscriptionEvent.startDate.getHours() > 9
                                ? subscriptionEvent.startDate.getHours()
                                : '0' + subscriptionEvent.startDate.getHours()
                            const minutes =
                              subscriptionEvent.startDate.getMinutes() > 9
                                ? subscriptionEvent.startDate.getMinutes()
                                : '0' + subscriptionEvent.startDate.getMinutes()
                            const seconds =
                              subscriptionEvent.startDate.getSeconds() > 9
                                ? subscriptionEvent.startDate.getSeconds()
                                : '0' + subscriptionEvent.startDate.getSeconds()
                            const dtStart = subscriptionEvent.startDate
                              ? `DTSTART=${year}${month}${date}T${hours}${minutes}${seconds};`
                              : ''
                              subscriptionEvent.rrule = `${dtStart}${subscriptionEvent.rrule}`
                          }
                        }
                      )

                      processedSubscriptions += 1
                      if (
                        processedSubscriptions ==
                        context.getters.currentSubscriptions.length
                      ) {
                        resolve()
                      }
                    },
                    (error) => {
                      Vue.prototype.$toast.add(
                        new AlertMessage(
                          AlertMessageSeverity.error,
                          'Error Fetching Subscription Events',
                          error
                        )
                      )
                      processedSubscriptions += 1
                      if (
                        processedSubscriptions ==
                        context.getters.currentSubscriptions.length
                      ) {
                        reject()
                      }
                    }
                  )
              } else {
                processedSubscriptions += 1
                if (
                  processedSubscriptions ==
                  context.getters.currentSubscriptions.length
                ) {
                  resolve()
                }
              }
            }
          )
        }
      })
    },
    mapDuplicateSubscriptionEvents: function (context) {
      return new Promise<void>((resolve, reject) => {
        if (!context.getters.currentSubscriptions.length) {
          resolve()
        } else {
          context.getters.currentSubscriptions.forEach(
            (subscription, subscriptionIndex) => {
              subscription.duplicateEventsMap = []
              subscription.events.forEach((event) => {
                context.getters.currentSubscriptions.forEach(
                  (testSubscription) => {
                    if (testSubscription.id.intID == subscription.id.intID) {
                      return
                    }
                    const duplicateEvents = testSubscription.events.filter(
                      (testEvent) => {
                        if (
                          event.title == testEvent.title &&
                          ((event.isAllDay && testEvent.isAllDay) ||
                            (event.startDate == testEvent.startDate &&
                              event.endDate == testEvent.endDate))
                        ) {
                          return true
                        }
                        return false
                      }
                    )
                    if (duplicateEvents.length) {
                      const formattedDuplicateEvents = duplicateEvents.map(
                        (duplicateEvent) => {
                          return new DuplicateSubscriptionEventMapping(
                            subscription.id.intID,
                            event.id.intID,
                            testSubscription.id.intID,
                            duplicateEvent.id.intID
                          )
                        }
                      )
                      subscription.duplicateEventsMap =
                        subscription.duplicateEventsMap.concat(
                          formattedDuplicateEvents
                        )
                    }
                  }
                )
              })

              if (
                subscriptionIndex + 1 ==
                context.getters.currentSubscriptions.length
              ) {
                resolve()
              }
            }
          )
        }
      })
    },

    documentToCategoriesForCurrentPlan: function (context) {
      const plan = context.getters.currentPlan
      const documents: PlanDocument[] = context.getters.currentPlan.documents
      const planDocumentCategories = context.getters.currentPlan.planDocumentCategories

      if (
        planDocumentCategories[planDocumentCategories.length - 1]?.id?.intID !==
        0
      ) {
        planDocumentCategories.push(new PlanDocumentCategory('Uncategorized'))
      }

      // IDs of the "real" (persisted) categories on this plan, used to detect
      // documents that don't belong to any of them (i.e. are uncategorized).
      const realCategoryIds = planDocumentCategories
        .map((category) => category.id?.intID)
        .filter((intID) => !!intID)

      plan.planDocumentCategories = planDocumentCategories.map((category) => {
        const isUncategorized = category.name === 'Uncategorized'

        category.planDocuments = documents.filter((doc) => {
          const docCategoryIds = (doc.categories ?? [])
            .map((c) => c.id?.intID)
            .filter((intID) => !!intID)

          if (isUncategorized) {
            // A document is uncategorized when it references no real category.
            // Documents uploaded from a tactic or initiative have no category,
            // so this is how they surface in the Documents view.
            return !docCategoryIds.some((intID) =>
              realCategoryIds.includes(intID)
            )
          }

          return docCategoryIds.includes(category.id?.intID)
        })

        return category
      }) as PlanDocumentCategory[]

      context.dispatch('refreshCurrentPlan', plan)
    },

    //-----------------------------------------------//
    //Manage filters and localstorage
    //-----------------------------------------------//
    readQueryParams: function (context, queryParams) {
      return new Promise<void>((resolve, reject) => {
        //Test query string: ?ds-47=1-1-2021&de-47=12-31-2021&ch-47=12,13&in-47=21&tg-47=33,32&sb-47=44
        Object.keys(queryParams).forEach((param) => {
          if (queryParams[param]) {
            window.localStorage.setItem(param, queryParams[param])
          } else {
            window.localStorage.removeItem(param)
          }
        })
        resolve()
      })
    },
    setSelectedPlanPropertiesFromLocalStorage: function (context) {
      return new Promise<void>((resolve, reject) => {
        const filters = {
          lpl: window.localStorage.getItem(
            'lpl-' + context.getters.currentPlanIntID.toString()
          ),
          npl: window.localStorage.getItem(
            'npl-' + context.getters.currentPlanIntID.toString()
          ),
          tp: window.localStorage.getItem(
            'tp-' + context.getters.currentPlanIntID.toString()
          ),
          in: window.localStorage.getItem(
            'in-' + context.getters.currentPlanIntID.toString()
          ),
          ni: window.localStorage.getItem(
            'ni-' + context.getters.currentPlanIntID.toString()
          ),
          tg: window.localStorage.getItem(
            'tg-' + context.getters.currentPlanIntID.toString()
          ),
          nt: window.localStorage.getItem(
            'nt-' + context.getters.currentPlanIntID.toString()
          ),
          sb: window.localStorage.getItem(
            'sb-' + context.getters.currentPlanIntID.toString()
          ),
        }

        //Lead Plan
        context.commit('updateIsLeadPlanVisible', filters.lpl === 'false' ? false : true)

        //Nested Plans
        if (filters.npl !== null) {
          const nestedPlanFilterIds = filters.npl.split(',')
          if (nestedPlanFilterIds.length > 0 && nestedPlanFilterIds.length < context.getters.currentPlan.plans.length) {
            context.commit('updateVisibleNestedPlansIntIds', nestedPlanFilterIds.filter((idString) => idString !== '').map((idString => parseInt(idString))))
            context.commit('isNestedPlanFilterActive', true)
          }else{
            const planIds = context.getters.currentPlan.plans.length ? context.getters.currentPlan.plans.map((planId) => planId.intID) : [context.getters.currentPlan.id.intID]
            context.commit('updateVisibleNestedPlansIntIds', planIds)
            context.commit('isNestedPlanFilterActive', false)
          }
        }else{
          const planIds = context.getters.currentPlan.plans.length ? context.getters.currentPlan.plans.map((planId) => planId.intID) : [context.getters.currentPlan.id.intID]
          context.commit('updateVisibleNestedPlansIntIds', planIds)
          context.commit('isNestedPlanFilterActive', false)
        }

        // Tactic Types
        if (filters.tp !== null) {
          if (filters.tp.length > 0) {
            const filterIds = filters.tp.split(',')
            context.commit(
              'updateSelectedTacticTypes',
              context.getters.currentTacticTypes.filter(
                (type) => filterIds.indexOf(type.id.intID.toString()) > -1
              )
            )
            context.commit('isChannelFilterActive', true)
          } else {
            context.commit(
              'updateSelectedTacticTypes',
              context.getters.currentTacticTypes
            )
            context.commit('isChannelFilterActive', false)
          }
        } else {
          context.commit(
            'updateSelectedTacticTypes',
            context.getters.currentTacticTypes
          )
          context.commit('isChannelFilterActive', false)
        }
        //Initiatives
        if (filters.ni === 'true') {
          context.commit('filterNoInitiative', true)
          context.commit('updateSelectedInitiatives', [])
          context.commit('updateSelectedInitiativeCustomizations', [])
          context.commit('isInitiativeFilterActive', true)
        } else {
          context.commit('filterNoInitiative', false)
          if (filters.in !== null) {
            const filterIds = filters.in.split(',')
            if (filters.in.length > 0) {
              context.commit('updateSelectedInitiatives', context.getters.currentInitiatives.filter((initiative) => filterIds.indexOf(initiative.id.intID.toString()) > -1))
              context.commit('updateSelectedInitiativeCustomizations', context.getters.currentInitiativeCustomizations)
              context.commit('isInitiativeFilterActive', true)
            } else {
              context.commit('updateSelectedInitiatives', context.getters.currentInitiatives)
              context.commit('updateSelectedInitiativeCustomizations', context.getters.currentInitiativeCustomizations)
              context.commit('isInitiativeFilterActive', false)
            }
          } else {
            context.commit('updateSelectedInitiatives', context.getters.currentInitiatives)
            context.commit('updateSelectedInitiativeCustomizations', context.getters.currentInitiativeCustomizations)
            context.commit('isInitiativeFilterActive', false)
          }
        }
        //Tags
        if (filters.nt === 'true') {
          context.commit('filterNoTag', true)
          context.commit('updateSelectedTags', [])
          context.commit('isTagFilterActive', true)
        } else {
          context.commit('filterNoTag', false)
          if (filters.tg !== null) {
            if (filters.tg.length > 0) {
              const filterIds = filters.tg.split(',')
              context.commit(
                'updateSelectedTags',
                context.getters.currentTags.filter(
                  (tag) => filterIds.indexOf(tag.id.intID.toString()) > -1
                )
              )
              context.commit('isTagFilterActive', true)
            } else {
              context.commit('updateSelectedTags', [])
              context.commit('isTagFilterActive', false)
            }
          } else {
            context.commit('updateSelectedTags', context.getters.currentTags)
            context.commit('isTagFilterActive', false)
          }
        }
        //Subscriptions
        if (filters.sb !== null) {
          if (filters.sb.length > 0) {
            const filterIds = filters.sb.split(',')
            context.commit(
              'updateSelectedSubscriptions',
              context.getters.currentSubscriptions.filter(
                (subscription) =>
                  filterIds.indexOf(subscription.id.intID.toString()) > -1
              )
            )
            context.commit('isSubscriptionFilterActive', true)
          } else {
            context.commit('updateSelectedSubscriptions', [])
            context.commit('isSubscriptionFilterActive', false)
          }
        } else {
          context.commit('updateSelectedSubscriptions', [])
          context.commit('isSubscriptionFilterActive', false)
        }

        // Update filters badge count
        context.dispatch('updateActiveFiltersCount')

        resolve()
      })
    },
    updateActiveFiltersCount: function(context){
      let newActiveFiltersCount = 0
      // Tactic Types
      if(context.getters.isChannelFilterActive){
        newActiveFiltersCount += context.getters.selectedTacticTypes.length
      }
      // Initiatives
      if(context.getters.isInitiativeFilterActive){
        if(context.getters.filterNoInitiative){
          newActiveFiltersCount += 1
        } else {
          newActiveFiltersCount += context.getters.selectedInitiatives.length
        }
      }
      // Tags
      if(context.getters.isTagFilterActive){
        if(context.getters.filterNoTag){
          newActiveFiltersCount += 1
        } else {
          newActiveFiltersCount += context.getters.selectedTags.length
        }
      }
      // Subscriptions
      if(context.getters.isSubscriptionFilterActive){
        newActiveFiltersCount += context.getters.selectedSubscriptions.length
      }
      // Removed lead/nested plans from filter panel badge count - 05.09.25 JV
      // Nested Plans
      // if(context.getters.currentPlan.plans.length > 0 && context.getters.visibleNestedPlansIntIds.length < context.getters.currentPlan.plans.length){
      //   newActiveFiltersCount += context.getters.currentPlan.plans.length - context.getters.visibleNestedPlansIntIds.length
      // }
      // Lead Plan
      // if(context.getters.currentPlan.plans.length === 0 && !context.getters.isLeadPlanVisible){
      //   newActiveFiltersCount += 1
      // }
      context.dispatch('setActiveFiltersCount', newActiveFiltersCount)
    },
    updateCurrentDateRange: function (context, payload: { newDateRange?: Date[], shouldFetchTactics?: boolean } = {}) {
      let { newDateRange, shouldFetchTactics } = payload;
      if (shouldFetchTactics === undefined) shouldFetchTactics = true;

      // Default to current month
      if(!newDateRange || !newDateRange.length){
        newDateRange = [new Date(), new Date()]
        newDateRange[0].setDate(1)
        newDateRange[1].setMonth(newDateRange[1].getMonth() + 1)
        newDateRange[1].setDate(0)
      }

      // Set to beginning and end of days in current month
      newDateRange[0].setHours(0,0,0,0)
      newDateRange[1].setHours(23,59,59,999)

      // Update date range properties
      Vue.set(context.state, 'currentDateRange', [...newDateRange])
      if(!context.state.recentDateRangesDictionary[context.getters.currentPlanIntID]){
        context.state.recentDateRangesDictionary[context.getters.currentPlanIntID] = {}
      }
      context.state.recentDateRangesDictionary[context.getters.currentPlanIntID]['main'] = [...newDateRange]

      if(shouldFetchTactics){
        context.dispatch('fetchTacticsForCurrentPlanInDateRange', context.getters.currentDateRange)
      }
    },
    updateMonthViewDateRange: function (context, payload: { newRange: Date[], shouldFetchTactics?: boolean }) {
      const { newRange } = payload;
      let { shouldFetchTactics } = payload;
      if (shouldFetchTactics === undefined) shouldFetchTactics = true;

      Vue.set(context.state, 'monthViewDateRange', newRange)
      if(!context.state.recentDateRangesDictionary[context.getters.currentPlanIntID]){
        context.state.recentDateRangesDictionary[context.getters.currentPlanIntID] = {}
      }
      context.state.recentDateRangesDictionary[context.getters.currentPlanIntID]['monthView'] = [...newRange]

      if(shouldFetchTactics){
        context.dispatch('fetchTacticsForCurrentPlanInDateRange', context.getters.monthViewDateRange)
      }
    },
    updateDayViewDateRange: function (context, payload: { newRange: Date[], shouldFetchTactics?: boolean }) {
      const { newRange } = payload;
      let { shouldFetchTactics } = payload;
      if (shouldFetchTactics === undefined) shouldFetchTactics = true;

      Vue.set(context.state, 'dayViewDateRange', newRange)
      if(!context.state.recentDateRangesDictionary[context.getters.currentPlanIntID]){
        context.state.recentDateRangesDictionary[context.getters.currentPlanIntID] = {}
      }
      context.state.recentDateRangesDictionary[context.getters.currentPlanIntID]['dayView'] = [...newRange]

      if(shouldFetchTactics){
        context.dispatch('fetchTacticsForCurrentPlanInDateRange', context.getters.dayViewDateRange)
      }
    },
    updateRangeViewsDateRange: function (context, payload: { newRange: Date[], shouldFetchTactics?: boolean }) {
      const { newRange } = payload;
      let { shouldFetchTactics } = payload;
      if (shouldFetchTactics === undefined) shouldFetchTactics = true;

      Vue.set(context.state, 'rangeViewsDateRange', newRange)
      if(!context.state.recentDateRangesDictionary[context.getters.currentPlanIntID]){
        context.state.recentDateRangesDictionary[context.getters.currentPlanIntID] = {}
      }
      context.state.recentDateRangesDictionary[context.getters.currentPlanIntID]['rangeViews'] = [...newRange]

      if(shouldFetchTactics){
        context.dispatch('fetchTacticsForCurrentPlanInDateRange', context.getters.rangeViewsDateRange)
      }
    },
    updateMonthRangeViewsDateRange: function (context, payload: { newRange: Date[], shouldFetchTactics?: boolean }) {
      const { newRange } = payload;
      let { shouldFetchTactics } = payload;
      if (shouldFetchTactics === undefined) shouldFetchTactics = true;

      Vue.set(context.state, 'monthRangeViewsDateRange', newRange)
      if(!context.state.recentDateRangesDictionary[context.getters.currentPlanIntID]){
        context.state.recentDateRangesDictionary[context.getters.currentPlanIntID] = {}
      }
      context.state.recentDateRangesDictionary[context.getters.currentPlanIntID]['monthRangeViews'] = [...newRange]

      if(shouldFetchTactics){
        context.dispatch('fetchTacticsForCurrentPlanInDateRange', context.getters.monthRangeViewsDateRange)
      }
    },
    updateInitiativeSettingsDateRange: function (context, payload: { newRange: Date[], shouldFetchInitiatives?: boolean }) {
      const { newRange } = payload;
      let { shouldFetchInitiatives } = payload;
      if (shouldFetchInitiatives === undefined) shouldFetchInitiatives = true;

      Vue.set(context.state, 'initiativeSettingsDateRange', newRange)
      if(!context.state.recentDateRangesDictionary[context.getters.currentPlanIntID]){
        context.state.recentDateRangesDictionary[context.getters.currentPlanIntID] = {}
      }
      context.state.recentDateRangesDictionary[context.getters.currentPlanIntID]['initiativeSettings'] = [...newRange]

      // Fetch initiatives for current plan in date range
      if(shouldFetchInitiatives){
        context.dispatch('fetchInitiativesForCurrentPlanInDateRange', newRange)
      }
    }, 
    setAllDateRanges: function (context, payload: { newRange?: Date[], shouldFetchTactics?: boolean } = {}){
      const { newRange, shouldFetchTactics = true } = payload

      if(newRange && newRange.length){
        context.dispatch('updateCurrentDateRange', { newDateRange: newRange, shouldFetchTactics: shouldFetchTactics })
      }else{
        context.dispatch('updateCurrentDateRange', { shouldFetchTactics: shouldFetchTactics })
      }
      context.dispatch('updateRangeViewsDateRange', { newRange: context.getters.currentDateRange, shouldFetchTactics: false })
      context.dispatch('updateMonthViewDateRange', { newRange: context.getters.currentDateRange, shouldFetchTactics: false })
      context.dispatch('updateDayViewDateRange', { newRange: context.getters.currentDateRange, shouldFetchTactics: false })

      // Set initiative settings range to 12 months from current date
      const initiativesRangeStart = new Date()
      initiativesRangeStart.setHours(0, 0, 0, 0)
      const initiativesRangeEnd = new Date()
      initiativesRangeEnd.setMonth(initiativesRangeStart.getMonth() + 12)
      initiativesRangeEnd.setHours(23, 59, 59, 999)
      context.dispatch('updateInitiativeSettingsDateRange', { newRange: [initiativesRangeStart, initiativesRangeEnd], shouldFetchInitiatives: false })

      // Set monthRangeViewsDateRange to 12 months from start of current month
      const monthRangeStart = new Date()
      monthRangeStart.setDate(1)
      monthRangeStart.setHours(0, 0, 0, 0)
      const monthRangeEnd = new Date(monthRangeStart)
      monthRangeEnd.setMonth(monthRangeStart.getMonth() + 12, 0)
      monthRangeEnd.setHours(23, 59, 59, 999)
      context.dispatch('updateMonthRangeViewsDateRange', { newRange: [monthRangeStart, monthRangeEnd], shouldFetchTactics: false })
    },
    restoreAllDateRangesForCurrentPlan: function (context){
      context.dispatch('updateCurrentDateRange', { newDateRange: context.state.recentDateRangesDictionary[context.getters.currentPlanIntID]['main'], shouldFetchTactics: false })
      context.dispatch('updateRangeViewsDateRange', { newRange: context.state.recentDateRangesDictionary[context.getters.currentPlanIntID]['rangeViews'], shouldFetchTactics: false })
      context.dispatch('updateMonthViewDateRange', { newRange: context.state.recentDateRangesDictionary[context.getters.currentPlanIntID]['monthView'], shouldFetchTactics: false })
      context.dispatch('updateDayViewDateRange', { newRange: context.state.recentDateRangesDictionary[context.getters.currentPlanIntID]['dayView'], shouldFetchTactics: false })
      context.dispatch('updateInitiativeSettingsDateRange', { newRange: context.state.recentDateRangesDictionary[context.getters.currentPlanIntID]['initiativeSettings'], shouldFetchInitiatives: false })
      context.dispatch('updateMonthRangeViewsDateRange', { newRange: context.state.recentDateRangesDictionary[context.getters.currentPlanIntID]['monthRangeViews'], shouldFetchInitiatives: false })
    },
    updateFeedbackModal: function (context, isOpen: boolean) {
      context.commit('updateIsFeedbackModalOpen', isOpen)
    },
    addRecurringEventClones: function (context, subscription) {
      subscription.events.forEach((tactic) => {
        if (!tactic.rrule) {
          return
        }
        const diff = differenceInMinutes(tactic.endDate, tactic.startDate)
        const rules = rrulestr(tactic.rrule)

        // Adjust start and end dates to cover events that start or end in the precending or subsequent month from the current date range
        const testStartDate = new Date(context.state.currentDateRange[0])
        testStartDate.setMonth(testStartDate.getMonth() - 1)
        testStartDate.setDate(0)
        testStartDate.setHours(-1)
        const testEndDate = new Date(context.state.currentDateRange[1])
        testEndDate.setMonth(testEndDate.getMonth() + 1)
        testEndDate.setDate(1)
        
        const recurrenceDates = rules
          .between(
            testStartDate,
            testEndDate
          )
          .filter((recurrenceStartDate) => {
            // Filter out original tactic and any clones prior to it
            return recurrenceStartDate > tactic.startDate
          })

        if (recurrenceDates.length) {
          let newRecurrences = recurrenceDates.map((recurrenceDate, i) => {
            // Account for timezone offset
            recurrenceDate = new Date(recurrenceDate.getTime() + (recurrenceDate.getTimezoneOffset() * 60000));
            
            return {
              ...tactic,
              id: new ID(Number(tactic.id.intID.toString() + '0000' + i.toString())),
              startDate: recurrenceDate,
              endDate: addMinutes(recurrenceDate, diff),
              isRecurringEventClone: true,
            }
          })

          // need to filter out here because on refresh it tries to add them again
          newRecurrences = newRecurrences.filter((newRecurrence) => {
            const match = subscription.events.find((t) => {
              return (
                isSameDay(newRecurrence.startDate, t.startDate) &&
                tactic.id.apiID === t.id.apiID
              )
            })
            return !match
          })

          subscription.events = subscription.events.concat(newRecurrences)
        }
      })
    },
    setActiveFiltersCount(context, count) {
      Vue.set(
        context.state,
        'activeFiltersCount',
        count == '0' ? '' : count.toString()
      )
    },
    getViewRoles(context) {
      return new Promise((resolve, reject) => {
        context.state.services.plans
          .getViewedRoles()
          .then((response) => {
            resolve(true)
          })
          .catch((error) => reject(error))
      })
    },
    updateViewRoles(context, payload) {
      return new Promise((resolve, reject) => {
        context.state.services.plans
          .updateVisitedPlan(payload)
          .then((response) => {
            resolve(response)
          })
          .catch((error) => reject(error))
      })
    },

    getPlanFromID: (context, payload) => {
      return new Promise((resolve, reject) => {
        context.state.services.plans
          .get(payload)
          .then((response) => {
            resolve(response)
          })
          .catch((error) => reject(error))
      })
    },

    //-----------------------------------------------//
    //API communication pass-throughs
    //-----------------------------------------------//

    //Account Service
    // createAccount(context, newAccount: Account) {
    //   return context.state.services.accounts.create(newAccount).then(
    //     (response) => {
    //       return response
    //     },
    //     (error) => {
    //       Vue.prototype.$toast.add(
    //         new AlertMessage(AlertMessageSeverity.error, error)
    //       )
    //     }
    //   )
    // },

    // getAccount(context, ids: number[]) {
    //   return context.state.services.accounts.get(ids).then(
    //     (response) => {
    //       return response
    //     },
    //     (error) => {
    //       Vue.prototype.$toast.add(
    //         new AlertMessage(AlertMessageSeverity.error, error)
    //       )
    //     }
    //   )
    // },

    // updateAccount(context, updatedAccount: Account) {
    //   return context.state.services.accounts.update(updatedAccount).then(
    //     (response) => {
    //       return response
    //     },
    //     (error) => {
    //       Vue.prototype.$toast.add(
    //         new AlertMessage(AlertMessageSeverity.error, error)
    //       )
    //     }
    //   )
    // },

    // deleteAccount(context, ids: number[]) {
    //   return context.state.services.accounts.delete(ids).then(
    //     (response) => {
    //       return response
    //     },
    //     (error) => {
    //       Vue.prototype.$toast.add(
    //         new AlertMessage(AlertMessageSeverity.error, error)
    //       )
    //     }
    //   )
    // },

    // getAccountRoles(context) {
    //   return context.state.services.accounts.getRoles().then(
    //     (response) => {
    //       return response
    //     },
    //     (error) => {
    //       Vue.prototype.$toast.add(
    //         new AlertMessage(AlertMessageSeverity.error, error)
    //       )
    //     }
    //   )
    // },

    // getAccountSubscriptions(context) {
    //   return context.state.services.accounts.getSubscriptions().then(
    //     (response) => {
    //       return response
    //     },
    //     (error) => {
    //       Vue.prototype.$toast.add(
    //         new AlertMessage(AlertMessageSeverity.error, error)
    //       )
    //     }
    //   )
    // },

    //Channel Service
    // createChannel(context, newChannel: Channel) {
    //   return context.state.services.channels.create(newChannel).then(
    //     (response) => {
    //       return response
    //     },
    //     (error) => {
    //       Vue.prototype.$toast.add(
    //         new AlertMessage(AlertMessageSeverity.error, error)
    //       )
    //     }
    //   )
    // },

    // getChannel(context, ids: number[]) {
    //   return context.state.services.channels.get(ids).then(
    //     (response) => {
    //       return response
    //     },
    //     (error) => {
    //       Vue.prototype.$toast.add(
    //         new AlertMessage(AlertMessageSeverity.error, error)
    //       )
    //     }
    //   )
    // },

    // updateChannel(context, updatedChannel: Channel) {
    //   return context.state.services.channels.update(updatedChannel).then(
    //     (response) => {
    //       return response
    //     },
    //     (error) => {
    //       Vue.prototype.$toast.add(
    //         new AlertMessage(AlertMessageSeverity.error, error)
    //       )
    //     }
    //   )
    // },

    // deleteChannel(context, ids: number[]) {
    //   return context.state.services.channels.delete(ids).then(
    //     (response) => {
    //       return response
    //     },
    //     (error) => {
    //       Vue.prototype.$toast.add(
    //         new AlertMessage(AlertMessageSeverity.error, error)
    //       )
    //     }
    //   )
    // },

    // createTacticCategory(context, newTacticCategory: TacticCategory) {
    //   return context.state.services.channels
    //     .createTacticCategory(newTacticCategory)
    //     .then(
    //       (response) => {
    //         return response
    //       },
    //       (error) => {
    //         Vue.prototype.$toast.add(
    //           new AlertMessage(AlertMessageSeverity.error, error)
    //         )
    //       }
    //     )
    // },

    // CommentService
    // createComment(context, newComment: Comment) {
    //   return context.state.services.comments.create(newComment).then(
    //     (response) => {
    //       return response
    //     },
    //     (error) => {
    //       Vue.prototype.$toast.add(
    //         new AlertMessage(AlertMessageSeverity.error, error)
    //       )
    //     }
    //   )
    // },

    // getComment(context, ids: number[]) {
    //   return context.state.services.comments.get(ids).then(
    //     (response) => {
    //       return response
    //     },
    //     (error) => {
    //       Vue.prototype.$toast.add(
    //         new AlertMessage(AlertMessageSeverity.error, error)
    //       )
    //     }
    //   )
    // },

    // updateComment(context, updatedComment: Comment) {
    //   return context.state.services.comments.update(updatedComment).then(
    //     (response) => {
    //       return response
    //     },
    //     (error) => {
    //       Vue.prototype.$toast.add(
    //         new AlertMessage(AlertMessageSeverity.error, error)
    //       )
    //     }
    //   )
    // },

    // deleteComment(context, ids: number[]) {
    //   return context.state.services.comments.delete(ids).then(
    //     (response) => {
    //       return response
    //     },
    //     (error) => {
    //       Vue.prototype.$toast.add(
    //         new AlertMessage(AlertMessageSeverity.error, error)
    //       )
    //     }
    //   )
    // },

    //Tactics Service
    // createTactic(context, newTactic: Tactic) {
    //   return context.state.services.tactics.create(newTactic).then(
    //     (response) => {
    //       return response
    //     },
    //     (error) => {
    //       Vue.prototype.$toast.add(
    //         new AlertMessage(AlertMessageSeverity.error, error)
    //       )
    //     }
    //   )
    // },

    // getTactic(context, ids: number[]) {
    //   return context.state.services.tactics.get(ids).then(
    //     (response) => {
    //       return response
    //     },
    //     (error) => {
    //       Vue.prototype.$toast.add(
    //         new AlertMessage(AlertMessageSeverity.error, error)
    //       )
    //     }
    //   )
    // },

    // updateTactic(context, updatedTactic: Tactic) {
    //   return context.state.services.tactics.update(updatedTactic).then(
    //     (response) => {
    //       return response
    //     },
    //     (error) => {
    //       Vue.prototype.$toast.add(
    //         new AlertMessage(AlertMessageSeverity.error, error)
    //       )
    //     }
    //   )
    // },

    // deleteTactic(context, ids: number[]) {
    //   return context.state.services.tactics.delete(ids).then(
    //     (response) => {
    //       return response
    //     },
    //     (error) => {
    //       Vue.prototype.$toast.add(
    //         new AlertMessage(AlertMessageSeverity.error, error)
    //       )
    //     }
    //   )
    // },

    // importTactic(context, fileBinary: string) {
    //   return context.state.services.tactics.import(fileBinary).then(
    //     (response) => {
    //       return response
    //     },
    //     (error) => {
    //       Vue.prototype.$toast.add(
    //         new AlertMessage(AlertMessageSeverity.error, error)
    //       )
    //     }
    //   )
    // },

    // searchTactic(context, params) {
    //   return context.state.services.tactics
    //     .search(
    //       params.planId,
    //       params.keyword,
    //       params.startDate,
    //       params.endDate,
    //       params.userId,
    //       params.channelId,
    //       params.typeId
    //     )
    //     .then(
    //       (response) => {
    //         return response
    //       },
    //       (error) => {
    //         Vue.prototype.$toast.add(
    //           new AlertMessage(AlertMessageSeverity.error, error)
    //         )
    //       }
    //     )
    // },

    //User service
    // createUser(context, newUser: User) {
    //   return context.state.services.users.create(newUser).then(
    //     (response) => {
    //       return response
    //     },
    //     (error) => {
    //       Vue.prototype.$toast.add(
    //         new AlertMessage(AlertMessageSeverity.error, error)
    //       )
    //     }
    //   )
    // },

    // getUser(context, ids: number[]) {
    //   return context.state.services.users.get(ids).then(
    //     (response) => {
    //       return response
    //     },
    //     (error) => {
    //       Vue.prototype.$toast.add(
    //         new AlertMessage(AlertMessageSeverity.error, error)
    //       )
    //     }
    //   )
    // },

    // updateUser(context, updatedUser: User) {
    //   return context.state.services.users.update(updatedUser).then(
    //     (response) => {
    //       return response
    //     },
    //     (error) => {
    //       Vue.prototype.$toast.add(
    //         new AlertMessage(AlertMessageSeverity.error, error)
    //       )
    //     }
    //   )
    // },

    // deleteUser(context, userToDelete: User) {
    //   return context.state.services.users.delete(userToDelete).then(
    //     (response) => {
    //       return response
    //     },
    //     (error) => {
    //       Vue.prototype.$toast.add(
    //         new AlertMessage(AlertMessageSeverity.error, error)
    //       )
    //     }
    //   )
    // },

    // inviteUser(context, newUserInvite: UserInvite) {
    //   return context.state.services.users.invite(newUserInvite).then(
    //     (response) => {
    //       return response
    //     },
    //     (error) => {
    //       Vue.prototype.$toast.add(
    //         new AlertMessage(AlertMessageSeverity.error, error)
    //       )
    //     }
    //   )
    // },

    // deleteUserInvite(context, ids: number[]) {
    //   return context.state.services.users.deleteUserInvite(ids).then(
    //     (response) => {
    //       return response
    //     },
    //     (error) => {
    //       Vue.prototype.$toast.add(
    //         new AlertMessage(AlertMessageSeverity.error, error)
    //       )
    //     }
    //   )
    // },

    // forgotPassword(context, emailAddress: string) {
    //   return context.state.services.users.forgotPassword(emailAddress).then(
    //     (response) => {
    //       return response
    //     },
    //     (error) => {
    //       Vue.prototype.$toast.add(
    //         new AlertMessage(AlertMessageSeverity.error, error)
    //       )
    //     }
    //   )
    // },

    // mediaAsset(context, file) {
    //   return context.state.services.users.mediaAsset(file).then(
    //     (response) => {
    //       return response
    //     },
    //     (error) => {
    //       Vue.prototype.$toast.add(
    //         new AlertMessage(AlertMessageSeverity.error, error)
    //       )
    //     }
    //   )
    // },

    //TagService
    // createTag(context, newTactic: Tag) {
    //   return context.state.services.tags.create(newTactic).then(
    //     (response) => {
    //       return response
    //     },
    //     (error) => {
    //       Vue.prototype.$toast.add(
    //         new AlertMessage(AlertMessageSeverity.error, error)
    //       )
    //     }
    //   )
    // },

    // getTag(context, ids: number[]) {
    //   return context.state.services.tags.get(ids).then(
    //     (response) => {
    //       return response
    //     },
    //     (error) => {
    //       Vue.prototype.$toast.add(
    //         new AlertMessage(AlertMessageSeverity.error, error)
    //       )
    //     }
    //   )
    // },

    // updateTag(context, updatedTactic: Tag) {
    //   return context.state.services.tags.update(updatedTactic).then(
    //     (response) => {
    //       return response
    //     },
    //     (error) => {
    //       Vue.prototype.$toast.add(
    //         new AlertMessage(AlertMessageSeverity.error, error)
    //       )
    //     }
    //   )
    // },

    // deleteTag(context, ids: number[]) {
    //   return context.state.services.tags.delete(ids).then(
    //     (response) => {
    //       return response
    //     },
    //     (error) => {
    //       Vue.prototype.$toast.add(
    //         new AlertMessage(AlertMessageSeverity.error, error)
    //       )
    //     }
    //   )
    // },
  },
  modules: {},
})
